import { useState, useMemo, useEffect, useRef } from "react";
import {
  ESCENDANTS, TOMES, IMPERA, UHLTIS,
  AFFINITIES, PROPHECIES, CURIOS, ESCLINGS,
  CARD_TYPES, SCHOOLS, ILKS,
} from "./cards";
import {
  SD, shimmerBorder, TYPE_COLORS_SD,
  GM, OBSIDIAN_BG, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  OBSIDIAN_PANEL, GOLD_METAL_H, facetTB,
  SHADOW_RAISED, SHADOW_RECESS, SHADOW_FLOAT, textGlow, TEXT_SET,
} from "./theme";
// ⭐ IMPORTED, NOT RETYPED — see the note where the local literals used
// to be. rules.js calls itself the single source of truth for these.
import { GRIMOIRE_MIN, LUXMOIRE_MIN, LUXMOIRE_MAX, LUXMOIRE_UHLTI_MAX, ESCLING_SLOTS } from "./rules";
import { useModalKeys } from "./KeyboardNav";
import { HOLO_TIER_INFO, TIER_ORDER, TIER_ORDER_ALL } from "./PackArt";
import HoloFoil, { holoGlowStyle } from "./HoloFoil";
import TerrorFrame from "./TerrorFrame";
import { StatGlyph, StatChip } from "./StatGlyph";
import { getCardOrigins } from "./progression";
import SourceSigil, { getSourceLabel, primaryOrigin } from "./SourceSigil";
import TheMark from "./TheMark";
import { listenToProfile, readSets, scrapCopies, scrapValueOf } from "./progression";
import { CoinChip, crumbleToCoins } from "./CoinGlyph"; // T21 scrap chip + D16 crumble
import { STARTER_DECKS } from "./starterDecks"; // single source of truth for "what's actually in this deck" — the starter-deck banner reads it directly rather than hand-maintaining a second card-count table
import { cardUrl } from "./CDN"; // CDN law — one domain, one file (loose-debt sweep 2026-08-18)
import { useStageRect } from "./RoomBackdrop"; // C5 — the one canonical room rectangle
import { COMPANIONS, getUnlockedCompanions, ILK_COLORS as COMPANION_ILK_COLORS } from "./companions";

// ── THE LANES (caP'n, 2026-08-26) ─────────────────────────────
//   *"the way the game currently works is correct. Grimoire —
//   Escendants and Tomes only. Luxmoire — everything else that doesn't
//   immediately get set to the board, like Curio and Prophecy."*
//
// Grimoire = Escendants + Tomes ONLY.
// Luxmoire = Impera + U'hltis + Affinities (10–40; max 1 Affinity R60,
//            max 3 U'hltis R104, Impera 5/4/3 by level R94).
// Esclings, Prophecies, and Curios are PRE-PLACED — never deck contents.
//
// ⚠ THE CITATION THAT STOOD HERE — "Rulez v2 LANES (July 2026)" — WAS
// FALSE. Rulez v2 is the APRIL book and its ch.3 says the OPPOSITE:
// U'hltis, Curios, Prophecies and Affinities all in the Grimoire, and a
// Luxmoire that "contains only Primera". The lanes below are the
// SHIPPING GAME's, which caP'n has now ruled correct; the April text is
// wrong, not merely old.
const ALL_GRIMOIRE_CARDS = [...ESCENDANTS, ...TOMES];
const ALL_LUXMOIRE_CARDS = [...IMPERA, ...UHLTIS, ...AFFINITIES];
// Fast id → card lookup across EVERY pool (incl. pre-placed types, so
// legacy saved decks still resolve for the grandfather-on-load path).
const CARD_BY_ID = Object.fromEntries(
  [...ALL_GRIMOIRE_CARDS, ...ALL_LUXMOIRE_CARDS, ...CURIOS, ...PROPHECIES, ...ESCLINGS].map(c => [c.id, c])
);
// GRIMOIRE_MIN / LUXMOIRE_MIN / LUXMOIRE_MAX used to be three literals
// right here — a second copy of numbers rules.js already owns. They
// happened to agree today. R06's ceiling moved from 30 to 40 in Rulez
// v2 and this file was updated by hand; the NEXT change would have been
// the one somebody missed, in the screen where a player builds the deck
// the rule is about. They are imported at the top now.
const STORAGE_KEY = "terror_tcg_grimoires";

// ── GILDED OBSIDIAN RE-FORGE (July 5, 2026) ──────────────────
// GM now imports from theme.js v4 — the local vellum-brown copy and
// MANUSCRIPT_BG are DEAD (inkSepia / waxSeal / ruling2 were unused).
// One palette, one source of truth (StartMenu / Plaza / Shop law).

// ── FORGE KEYFRAMES — imperative injection to document.head, keyed
// off node existence (Material Law 6 — never <style> blocks). ──
function ensureForgeKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("fg-keyframes")) return;
  const el = document.createElement("style");
  el.id = "fg-keyframes";
  el.textContent = `
    @keyframes fgRays { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes fgWash { 0%,100% { opacity: 0.5; transform: translate(0,0) scale(1); } 50% { opacity: 0.95; transform: translate(3%,-2%) scale(1.08); } }
    @keyframes fgWash2 { 0%,100% { opacity: 0.45; transform: translate(0,0) scale(1); } 50% { opacity: 0.85; transform: translate(-3%,2%) scale(1.1); } }
    @media (prefers-reduced-motion: reduce) {
      .fg-rays, .fg-wash { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureForgeKeyframes();

// ── SWIRL FIELD — the Forge breathes the same purple/red vortex
// air as the Divine Shop: a vast blurred conic swirl turning slowly
// plus two counter-breathing nebula washes. `fixed` pins it to the
// viewport for long scrolling screens (the classic builder).
function SwirlField({ accent = GM.amethyst, accent2 = "#dc2626", fixed = false }) {
  return (
    <div aria-hidden="true" style={{ position: fixed ? "fixed" : "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <div className="fg-rays" style={{
        position: "absolute", top: "50%", left: "50%",
        width: "165vmax", height: "165vmax", marginLeft: "-82.5vmax", marginTop: "-82.5vmax",
        background: `conic-gradient(from 0deg, transparent 0deg, ${accent}10 42deg, transparent 95deg, ${accent2}0c 150deg, transparent 205deg, ${accent}13 265deg, transparent 320deg)`,
        filter: "blur(26px)", animation: "fgRays 110s linear infinite",
      }} />
      <div className="fg-wash" style={{
        position: "absolute", top: "5%", left: "-12%", width: "70%", height: "46%",
        background: `radial-gradient(ellipse, ${accent}1e 0%, ${accent}0a 45%, transparent 70%)`,
        filter: "blur(26px)", animation: "fgWash 17s ease-in-out infinite",
      }} />
      <div className="fg-wash" style={{
        position: "absolute", bottom: "6%", right: "-14%", width: "62%", height: "42%",
        background: `radial-gradient(ellipse, ${accent2}18 0%, ${accent2}08 45%, transparent 70%)`,
        filter: "blur(24px)", animation: "fgWash2 22s ease-in-out infinite 4s",
      }} />
    </div>
  );
}

// ── Holo system constants ──────────────────────────────────────
// ── HOLO REGISTRY (derived from the master in PackArt.js) ──────
// HOLO_TIER_INFO + TIER_ORDER are the SINGLE source of truth for what
// foils exist. We derive label/color maps from it so the rest of this
// file's existing HOLO_COLORS[tier] / HOLO_LABELS[tier] lookups keep
// working — but now adding/renaming a holo means editing ONLY PackArt.js,
// never this file. Free Play's swatch list reads TIER_ORDER live, so a
// new tier shows up in the Forge automatically with zero work here.
const HOLO_COLORS = Object.fromEntries(
  Object.entries(HOLO_TIER_INFO).map(([t, i]) => [t, i.color])
);
const HOLO_LABELS = Object.fromEntries(
  Object.entries(HOLO_TIER_INFO).map(([t, i]) => [t, `${i.glyph} ${i.label}`])
);

// ─────────────────────────────────────────────────────────────
// ESCENDANT LINE DEFINITIONS — for Quick Build
// ─────────────────────────────────────────────────────────────
const ESCENDANT_LINES = [
  {
    id: "h01", name: "H-01", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE,
    icon: "⭐", color: "#60a5fa",
    tagline: "Sons of the Suns. Words first, power second.",
    beings: ["h01_beb", "h01_champion", "h01_lord"],
    impera: ["p_h01_1", "p_h01_2", "p_h01_3"],
    playstyle: "Defensive & Control",
  },
  {
    id: "h09", name: "H-09", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE,
    icon: "🌌", color: "#818cf8",
    tagline: "Generals of the Stars. Militancy is their first option.",
    beings: ["h09_beb", "h09_champion", "h09_lord"],
    impera: ["p_h09_1", "p_h09_2", "p_h09_3"],
    playstyle: "Aggressive & Military",
  },
  {
    id: "xulan", name: "Xulan", ilk: ILKS.HUMANOID, school: SCHOOLS.SPACE,
    icon: "🗡", color: "#a78bfa",
    tagline: "Assassins drifting through the Void seeking revenge.",
    beings: ["xulan_beb", "xulan_champion", "xulan_lord"],
    impera: ["p_xulan_1", "p_xulan_2", "p_xulan_3"],
    playstyle: "Stealth & Sacrifice",
  },
  {
    id: "spectral_tiger", name: "Spectral Tiger", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM,
    icon: "🐯", color: "#f472b6",
    tagline: "Kings of the Spirit Worlds. Haunting the Shadow Forest.",
    beings: ["spectral_tiger_beb", "spectral_tiger_champion", "spectral_tiger_lord"],
    impera: ["p_spectral_tiger_1", "p_spectral_tiger_2", "p_spectral_tiger_3"],
    playstyle: "Direct Anima Damage",
  },
  {
    id: "shadow_fox", name: "Shadow Fox", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM,
    icon: "🦊", color: "#c084fc",
    tagline: "Mischievous and elusive. Born in the deepest Shadow Spheres.",
    beings: ["shadow_fox_beb", "shadow_fox_champion", "shadow_fox_lord"],
    impera: ["p_shadow_fox_1", "p_shadow_fox_2", "p_shadow_fox_3"],
    playstyle: "Evasion & Intangible",
  },
  {
    id: "inverted_serpent", name: "Inverted Serpent", ilk: ILKS.BEAST, school: SCHOOLS.PHANTOM,
    icon: "🐍", color: "#34d399",
    tagline: "Protectors of the Shadow Forest's entrance. Do not be fooled.",
    beings: ["inverted_serpent_beb", "inverted_serpent_champion", "inverted_serpent_lord"],
    impera: ["p_inverted_serpent_1", "p_inverted_serpent_2", "p_inverted_serpent_3"],
    playstyle: "Sustained Anima Drain",
  },
  {
    id: "z4z", name: "Z4Z", ilk: ILKS.MACHINE, school: SCHOOLS.TIME,
    icon: "⚙", color: "#f87171",
    tagline: "Demons of destruction. They'll destroy anything for Khao.",
    beings: ["z4z_beb", "z4z_champion", "z4z_lord"],
    impera: ["p_z4z_1", "p_z4z_2", "p_z4z_3"],
    playstyle: "High Risk, High Reward",
  },
  {
    id: "dg0", name: "DG-0", ilk: ILKS.MACHINE, school: SCHOOLS.TIME,
    icon: "😇", color: "#fbbf24",
    tagline: "Healers of the Unstable World. Bringers of peace and prosperity.",
    beings: ["dg0_beb", "dg0_champion", "dg0_lord"],
    impera: ["p_dg0_1", "p_dg0_2", "p_dg0_3"],
    playstyle: "Anima Recovery & Draw",
  },
  {
    id: "antdroid", name: "Antdroid", ilk: ILKS.MACHINE, school: SCHOOLS.TIME,
    icon: "🤖", color: "#fb923c",
    tagline: "The most common Beb. They only seek Anima and eat without bias.",
    beings: ["antdroid_beb", "antdroid_champion", "antdroid_lord"],
    impera: ["p_antdroid_1", "p_antdroid_2", "p_antdroid_3"],
    playstyle: "Swarm & Multi-Attack",
  },
  {
    id: "lucky_dragon", name: "Lucky Dragon", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON,
    icon: "🎲", color: "#facc15",
    tagline: "Dealers of fate born in Shade. Almost any wish — at a cost.",
    beings: ["lucky_dragon_beb", "lucky_dragon_champion", "lucky_dragon_lord"],
    impera: ["p_lucky_dragon_1", "p_lucky_dragon_2", "p_lucky_dragon_3"],
    playstyle: "Shadow Gambling & Fortune",
  },
  {
    id: "premier_dragon", name: "Premier Dragon", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON,
    icon: "👑", color: "#4ade80",
    tagline: "The First & Only Leader. Born with Bloom. Deemed Worthy.",
    beings: ["premier_dragon_beb", "premier_dragon_champion", "premier_dragon_lord"],
    impera: ["p_premier_dragon_1", "p_premier_dragon_2", "p_premier_dragon_3"],
    playstyle: "Strength in Numbers",
  },
  {
    id: "chaos_dragon", name: "Chaos Dragon", ilk: ILKS.DRAGON, school: SCHOOLS.DRAGON,
    icon: "🌪", color: "#f43f5e",
    tagline: "The One & Only. Born with Doom. Even Xavizu called them a Threat.",
    beings: ["chaos_dragon_beb", "chaos_dragon_champion", "chaos_dragon_lord"],
    impera: ["p_chaos_dragon_1", "p_chaos_dragon_2", "p_chaos_dragon_3"],
    playstyle: "Pure Destruction",
  },
];

// Starter-only lines (Kyrium today, more later) have no Quick Build
// wizard entry — Kyrium's reactive-counter shape doesn't fit the
// beb/champ/lord fill math Quick Build does for the twelve lines above,
// and duplicating that math here would just be a second table to drift.
// But DECK STATUS below still needs to recognize them, or a perfectly
// legal Experimental deck shows a false "no Escendant line detected"
// warning the moment it's loaded. Derived straight from STARTER_DECKS —
// display-only, never touches Quick Build — so a future starter line
// is recognized here with zero edits.
const STARTER_ONLY_LINES = STARTER_DECKS
  .filter(deck => !ESCENDANT_LINES.some(l => l.id === deck.id.replace(/^starter_/, "")))
  .map(deck => {
    const beingIds = Object.keys(deck.grimoire || {})
      .filter(id => CARD_BY_ID[id]?.type === CARD_TYPES.ESCENDANT)
      .sort((a, b) => (CARD_BY_ID[a]?.level || 0) - (CARD_BY_ID[b]?.level || 0));
    if (beingIds.length !== 3) return null; // not a plain beb/champ/lord trio — skip rather than guess
    return { id: deck.id.replace(/^starter_/, ""), name: deck.name, icon: "✦", color: deck.color || SD.gold, beings: beingIds };
  })
  .filter(Boolean);

const ALL_ESCENDANT_LINES = [...ESCENDANT_LINES, ...STARTER_ONLY_LINES];

// Shared Tome base every deck should have
const SHARED_TOME_BASE = {
  tome_khao_garden: 3,
  tome_gauntlets_azka: 1,
  tome_wall_of_light: 1,
  tome_nothing_left: 1,
  tome_blood_in_blood_out: 1,
  tome_not_here: 1,
  tome_devils_advocate: 1,
  tome_seal_of_omnis: 1,
  tome_seal_of_nihil: 1,
  tome_soulcerers_seal: 1,
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${SD.purpleDim}, transparent)` }} />
      <div style={{ fontSize: 14, color: SD.gold }}>◆</div>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${SD.purpleDim}, transparent)` }} />
    </div>
  );
}

function ProgressBar({ value, min, max, color }) {
  const pct = Math.min(100, (value / (max || min)) * 100);
  const isValid = value >= min && (max === null || value <= max);
  const barColor = isValid ? color : value > 0 ? GM.inkGold : GM.inkBrown;
  return (
    <div style={{ height: 4, background: OBSIDIAN_RECESS, borderRadius: 3, boxShadow: SHADOW_RECESS, overflow: "hidden", marginTop: 4 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width 0.3s ease" }} />
    </div>
  );
}

function StatRow({ label, value, min, max = null, color = GM.inkGold, warn = null }) {
  const isValid = value >= min && (max === null || value <= max);
  const showWarn = !isValid && warn && value > 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, letterSpacing: 2, color: GM.vellumFaint, fontFamily: GM.font }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 900, color: isValid ? color : value > 0 ? GM.inkGold : GM.inkBrown, fontFamily: GM.font }}>
          {value}{max ? `/${max}` : `/${min}+`}
        </span>
      </div>
      <ProgressBar value={value} min={min} max={max} color={color} />
      {showWarn && <div style={{ fontSize: 10, color: GM.inkRed, marginTop: 3, fontStyle: "italic", fontFamily: GM.font }}>⚠ {warn}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DECK STATUS PANEL — used in both modes
// ─────────────────────────────────────────────────────────────
function DeckStatusPanel({ grimoire, luxmoire }) {
  const gCount = Object.values(grimoire).reduce((a, b) => a + b, 0);
  const lCount = Object.values(luxmoire).reduce((a, b) => a + b, 0);
  const gValid = gCount >= GRIMOIRE_MIN;
  const lValid = lCount >= LUXMOIRE_MIN && lCount <= LUXMOIRE_MAX;
  const valid = gValid && lValid;

  // Detect Escendant lines present -- ALL_ESCENDANT_LINES so a starter-only
  // line (Kyrium today) is recognized here even though it has no Quick
  // Build entry (see ALL_ESCENDANT_LINES' own note, above ESCENDANT_LINES).
  const lineStatus = ALL_ESCENDANT_LINES.map(line => {
    const [beb, champ, lord] = line.beings;
    const hasL1 = (grimoire[beb] || 0) > 0;
    const hasL2 = (grimoire[champ] || 0) > 0;
    const hasL3 = (grimoire[lord] || 0) > 0;
    const hasAny = hasL1 || hasL2 || hasL3;
    return { ...line, hasL1, hasL2, hasL3, hasAny, complete: hasL1 && hasL2 && hasL3 };
  }).filter(l => l.hasAny);

  // Warnings
  const warnings = [];
  if (lineStatus.length === 0) warnings.push("No Escendant line detected — add L1, L2, and L3 of the same line");
  lineStatus.forEach(l => {
    if (!l.hasL1) warnings.push(`${l.name}: missing Level 1`);
    if (!l.hasL2) warnings.push(`${l.name}: missing Level 2`);
    if (!l.hasL3) warnings.push(`${l.name}: missing Level 3`);
  });

  // Check Impera match a line
  const luxCards = Object.keys(luxmoire).filter(id => luxmoire[id] > 0);
  if (luxCards.length > 0 && lineStatus.length > 0) {
    const myLineIds = lineStatus.map(l => l.id);
    const mismatchedImpera = luxCards.filter(id => {
      const card = ALL_LUXMOIRE_CARDS.find(c => c.id === id);
      // THE LANES (caP'n, 2026-08-26): U'hltis/Affinities live here too
      // — only Impera carry line identity, so only they can "mismatch".
      return card && card.type === "Impera" && !myLineIds.some(lid => id.includes(lid));
    });
    if (mismatchedImpera.length > 0) warnings.push("Some Impera don't match your Escendant lines");
  }

  if (lCount > LUXMOIRE_MAX) warnings.push(`Luxmoire over limit — max ${LUXMOIRE_MAX} cards`);

  return (
    <div style={{ background: OBSIDIAN_SLAB, border: `1px solid ${valid ? GM.inkGold : GM.pageBorder}`, borderRadius: 10, padding: "14px 18px", boxShadow: valid ? `0 0 16px ${GM.inkGoldBright}44, inset 0 0 22px ${GM.inkGold}14, ${SHADOW_RAISED}` : SHADOW_RAISED }}>
      <div style={{ fontSize: 11, letterSpacing: 3, color: valid ? GM.inkGoldBright : GM.vellumFaint, marginBottom: 12, fontWeight: 900, fontFamily: GM.font, textShadow: valid ? textGlow(GM.inkGoldBright) : TEXT_SET }}>
        {valid ? "✦ GRIMOIRE SEALED" : "◆ DECK STATUS"}
      </div>

      <StatRow label="GRIMOIRE" value={gCount} min={GRIMOIRE_MIN} color={SD.purpleLight}
        warn={`Need ${GRIMOIRE_MIN - gCount} more cards`} />
      <StatRow label="LUXMOIRE" value={lCount} min={LUXMOIRE_MIN} max={LUXMOIRE_MAX} color={SD.gold}
        warn={lCount < LUXMOIRE_MIN ? `Need ${LUXMOIRE_MIN - lCount} more Luxmoire cards` : null} />

      {lineStatus.length > 0 && (
        <>
          <div style={{ fontSize: 10, letterSpacing: 2, color: SD.starDim, marginTop: 12, marginBottom: 8 }}>ESCENDANT LINES</div>
          {lineStatus.map(line => (
            <div key={line.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>{line.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: line.color, fontWeight: 900 }}>{line.name}</div>
                <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                  {["L1", "L2", "L3"].map((lvl, i) => {
                    const has = [line.hasL1, line.hasL2, line.hasL3][i];
                    return (
                      <div key={lvl} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: has ? line.color + "33" : SD.void3, color: has ? line.color : SD.void4, border: `1px solid ${has ? line.color + "66" : SD.void4}` }}>
                        {lvl} {has ? "✓" : "✗"}
                      </div>
                    );
                  })}
                </div>
              </div>
              {line.complete && <span style={{ fontSize: 12, color: line.color }}>✦</span>}
            </div>
          ))}
        </>
      )}

      {warnings.length > 0 && (
        <>
          <div style={{ fontSize: 10, letterSpacing: 2, color: GM.inkGoldDim, marginTop: 12, marginBottom: 6, fontFamily: GM.font }}>⚠ WARNINGS</div>
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", marginBottom: 4, lineHeight: 1.5, fontFamily: GM.font }}>• {w}</div>
          ))}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CARD DISPLAY — full card tile with real art
// ─────────────────────────────────────────────────────────────
// ── Card Art Lightbox ─────────────────────────────────────────
function CardLightbox({ card, holoTier = null, cardSources = null, sourceTiers = null, record = null, scrap = null, onClose }) {
  const [imgFailed, setImgFailed] = useState(false);
  // THE MARK (H18) — folded behind a toggle; default read stays a card.
  const [showMark, setShowMark] = useState(false);
  // T21/D16 — the art slab is what visibly crumbles when a copy is scrapped.
  const artRef = useRef(null);
  useModalKeys(onClose);
  if (!card) return null;
  const typeColor = TYPE_COLORS_SD[card.type] || SD.starDim;
  const holo = holoTier ? HOLO_COLORS[holoTier] : null;
  const glowStyle = holoTier ? holoGlowStyle(holoTier) : null;
  const borderColor = holo || typeColor + "88";
  // ── PROVENANCE — every source this card is owned in, with a total count
  // per source (flat `sources` + every foil's `sourceTiers` rolled up). This
  // is the loud-n-proud "where did this come from" row, shown ONLY here in the
  // view popup — never on the tile. {source: totalCount}.
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.96)", zIndex: 600, backdropFilter: "blur(8px)", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "160px 16px 40px" }}>
      <div style={{ width: "min(88vw, 340px)", fontFamily: SD.fontDisplay, display: "flex", flexDirection: "column" }}>
        {/* ── ORIGIN — the one-origin law, now worn BY the card: no text
            strip, just the sigil itself crowning the art (top-center).
            One mark, part of the card. */}
        {/* Full art — natural aspect ratio, just constrain width */}
        <div ref={artRef} style={{
          width: "100%", aspectRatio: "2/3",
          borderRadius: "14px 14px 0 0", overflow: "hidden", position: "relative",
          ...(glowStyle || {}),
          boxShadow: holo ? `0 0 0 2px ${holo}, 0 0 40px ${holo}66` : `0 0 0 1px ${typeColor}44`,
        }}>
          {!imgFailed ? (
            <img src={cardUrl(card.id)} alt={card.name} onError={() => setImgFailed(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", zIndex: 0 }} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: `linear-gradient(145deg,${SD.void3},${SD.void2})`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 36, color: typeColor }}>◆</div>
              <div style={{ fontSize: 14, color: typeColor, fontStyle: "italic" }}>{card.name}</div>
            </div>
          )}
          {holoTier && <HoloFoil tier={holoTier} radius={14} style={{ zIndex: 1, borderRadius: "14px 14px 0 0" }} />}
          {/* ORIGIN sigil — crowns the card, top-center. Just the mark. */}
          {(() => {
            const src = primaryOrigin({ sources: cardSources, sourceTiers }, getCardOrigins(card.id));
            if (!src) return null;
            return (
              <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                <SourceSigil source={src} size={30} />
              </div>
            );
          })()}
          {/* Holo tier pill — mirrors the sigil at bottom-center */}
          {holoTier && (
            <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", zIndex: 2,
              fontSize: 9, color: holo, fontWeight: 900, letterSpacing: 1, whiteSpace: "nowrap",
              background: "rgba(0,0,0,0.75)", padding: "2px 8px", borderRadius: 6,
              border: `1px solid ${holo}66`, textShadow: `0 0 8px ${holo}`,
              fontFamily: SD.fontDisplay }}>
              {HOLO_LABELS[holoTier]}
            </div>
          )}
        </div>
        {/* Info */}
        <div style={{ width: "100%", background: `linear-gradient(145deg,${SD.void2}f8,${SD.void1}fa)`, border: `1px solid ${typeColor}44`, borderRadius: "0 0 16px 16px", padding: "14px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 8, color: typeColor, letterSpacing: 3, fontWeight: 900 }}>{card.type?.toUpperCase()}{card.level ? ` · L${card.level}` : ""}</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: SD.starWhite, fontStyle: "italic" }}>{card.name}</div>
            </div>
            {card.type === "Escendant" && (
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: 7, color: SD.starDim }}>LYF</div><div style={{ fontSize: 18, fontWeight: 900, color: "#88ccff" }}>{card.lyf}</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontSize: 7, color: SD.starDim }}>LYT</div><div style={{ fontSize: 18, fontWeight: 900, color: "#f87171" }}>{card.lyt}</div></div>
              </div>
            )}
          </div>
          {(card.khaoCost > 0 || card.animaCost > 0) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              {card.khaoCost > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: SD.gold, background: `${SD.gold}18`, border: `1px solid ${SD.gold}44`, borderRadius: 4, padding: "1px 7px" }}><StatGlyph kind="khao" size={9} color={SD.gold} />{card.khaoCost} Khao</span>}
              {card.animaCost > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "#f87171", background: "#f8717118", border: "1px solid #f8717144", borderRadius: 4, padding: "1px 7px" }}><StatGlyph kind="anima" size={9} color="#f87171" />{card.animaCost} Anima</span>}
            </div>
          )}
          {card.effect && <div style={{ fontSize: 11, color: SD.starSilver, lineHeight: 1.7, marginBottom: 6 }}>{card.effectTag && <span style={{ color: typeColor, fontWeight: 900 }}>{card.effectTag}: </span>}{card.effect}</div>}
          {card.flavorText && <div style={{ fontSize: 10, color: SD.starDim, fontStyle: "italic", lineHeight: 1.5 }}>{card.flavorText}</div>}
          {/* ── THE MARK (H18) — summon the Empire's record on this card.
              The lightbox's props ARE the record slices (sources/
              sourceTiers); unowned cards fall back to canonical origins.
              Read-only, always. */}
          <button onClick={() => setShowMark(m => !m)} style={{
            width: "100%", marginTop: 12, padding: "8px",
            background: showMark ? `${GM.inkGold}18` : "transparent",
            border: `1px solid ${GM.inkGold}66`,
            borderRadius: 2, cursor: "pointer", color: GM.inkGold,
            fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2.5,
          }}>▦ THE MARK</button>
          {showMark && (
            <TheMark
              being={{ key: card.id, name: card.name, type: card.type, game: "Terror" }}
              record={{ sources: cardSources || undefined, sourceTiers: sourceTiers || undefined, holoTiers: holoTier ? { [holoTier]: 1 } : undefined }}
              origins={getCardOrigins(card.id)}
              width="100%"
              style={{ marginTop: 10 }}
            />
          )}
          {/* ── T21 · THE SCRAP LANE — the card-action surface's SCRAP.
              Manual always; one row per owned VARIANT (tier-aware, a scrap
              targets a specific copy); the floor of one keeps the last copy
              sacred. The CoinChip is the D16 mote target — the crumble is
              the whole receipt, so there is no toast and never will be. */}
          {scrap && record && (() => {
            const owned = record.count || 0;
            const tiers = record.holoTiers || {};
            const tierKeys = Object.keys(tiers).filter(t => (tiers[t] || 0) > 0)
              .sort((a, b) => {
                const ia = TIER_ORDER_ALL.indexOf(a), ib = TIER_ORDER_ALL.indexOf(b);
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
              });
            if (owned <= 0 && tierKeys.length === 0) return null;
            const plainDupes = Math.max(0, owned - 1);
            const rowStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: `1px solid ${SD.void3}` };
            const scrapBtn = (enabled, label, run, color) => (
              <button disabled={!enabled || scrap.busy} onClick={e => { e.stopPropagation(); run(); }} style={{
                background: enabled ? `${color}18` : "transparent",
                border: `1px solid ${enabled ? color : SD.void4}`,
                color: enabled ? color : SD.void4, borderRadius: 6,
                padding: "5px 10px", cursor: enabled && !scrap.busy ? "pointer" : "not-allowed",
                fontFamily: SD.fontDisplay, fontSize: 10, fontWeight: 900, letterSpacing: 1,
                opacity: scrap.busy ? 0.55 : 1, whiteSpace: "nowrap",
              }}>{label}</button>
            );
            return (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 9, letterSpacing: 3, fontWeight: 900, color: "#ff9c4a", fontFamily: SD.fontDisplay }}>♻ SCRAP LANE</span>
                  <CoinChip amount={scrap.coins} />
                </div>
                {owned > 0 && (
                  <div style={rowStyle}>
                    <span style={{ fontSize: 10, color: SD.starSilver, fontFamily: SD.fontDisplay }}>
                      Plain ×{owned}
                      {plainDupes === 0 && <span style={{ color: SD.starDim, fontStyle: "italic" }}> — last copy; the collection is the soul</span>}
                    </span>
                    {scrapBtn(plainDupes > 0, `♻ SCRAP 1 · ◕${scrapValueOf(null)}`, () => scrap.doScrap(card.id, null, artRef.current), "#ff9c4a")}
                  </div>
                )}
                {tierKeys.map(t => (
                  <div key={t} style={rowStyle}>
                    <span style={{ fontSize: 10, color: HOLO_COLORS[t] || SD.starSilver, fontWeight: 900, fontFamily: SD.fontDisplay }}>
                      {(HOLO_LABELS[t] || t).toUpperCase()} ×{tiers[t]}
                    </span>
                    {scrapBtn(true, `♻ SCRAP 1 · ◕${scrapValueOf(t)}`, () => scrap.doScrap(card.id, t, artRef.current), HOLO_COLORS[t] || "#ff9c4a")}
                  </div>
                ))}
              </div>
            );
          })()}
          <button onClick={onClose} style={{ width: "100%", marginTop: 12, padding: "9px", background: "transparent", border: `1px solid ${SD.void4}`, borderRadius: 8, cursor: "pointer", color: SD.starDim, fontFamily: SD.fontDisplay, fontSize: 11 }}>✕ CLOSE</button>
        </div>
      </div>
      </div>
    </div>
  );
}

function CardDisplay({
  card, count, onAdd, onRemove, maxAllowed, owned = true,
  holoTier = null, availableTiers = null, onSelectHolo = null,
  // NEW (provenance): for the numbered swatch + sigil picker.
  //   sourceTiers   collection[card.id].sourceTiers — {tier: {source: count}}.
  //                 Drives the per-tier number badge and the sigil sub-picker.
  //   cardSources   collection[card.id].sources — {source: count}. The FLAT
  //                 provenance (foil or not) — this is what shows the origin
  //                 sigil on PLAIN (non-foil) cards like the starter deck.
  //   onSelectVariant(cardId, tier, source) — apply a specific {tier, source}
  //                 instance to the deck (the multi-variant path).
  sourceTiers = null, cardSources = null, onSelectVariant = null,
  // T21 (scrap lane): record = collection[card.id] (counts for the lightbox
  // rows + the dupe test), scrap = the Forge's scrap context, marked/onToggleMark
  // = the multi-mark. All null outside the Forge — the tile is unchanged there.
  record = null, scrap = null, marked = false, onToggleMark = null,
}) {
  const imgUrl = cardUrl(card.id); // CDN law — one domain, one file
  const [lightbox, setLightbox] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // When a tier with multiple owned sources is tapped, we drop into a sigil
  // sub-picker for THAT tier. null = palette showing tier swatches.
  const [sigilTier, setSigilTier] = useState(null);
  const typeColor = TYPE_COLORS_SD[card.type] || SD.starDim;
  // How many distinct sources this card is owned in, for a given tier.
  const sourcesForTier = (t) => {
    const m = (sourceTiers && sourceTiers[t]) || null;
    if (!m) return [];
    return Object.keys(m).filter(src => (m[src] || 0) > 0);
  };
  const atMax = count >= maxAllowed;
  const CARD_W = 130;
  const CARD_H = 182;
  const holoColor = holoTier ? HOLO_COLORS[holoTier] : null;
  const holoLabel = holoTier ? HOLO_LABELS[holoTier] : null;
  // The breathing outer glow comes from the shared HoloFoil registry, so it
  // always matches the in-duel / shop / faction look for this tier.
  const glowStyle = holoTier ? holoGlowStyle(holoTier) : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div onClick={() => setLightbox(true)} data-scrap-tile={card.id} style={{
        position: "relative", width: CARD_W, height: CARD_H, borderRadius: 10, overflow: "hidden", cursor: "pointer",
        border: `1px solid ${holoColor ? holoColor : (count > 0 ? typeColor + "44" : typeColor + "22")}`,
        transition: "all 0.2s", opacity: owned ? 1 : 0.45, flexShrink: 0,
        ...(glowStyle || { boxShadow: "none" }),
        ...(marked ? { boxShadow: "0 0 0 2px #ff9c4a, 0 0 14px #ff9c4a88" } : {}),
      }}>
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(145deg, ${SD.void3}, ${SD.void2})`, zIndex: 0 }} />
        <img src={imgUrl} alt={card.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }} onError={e => { e.target.style.display = "none"; }} />
        {holoTier && <HoloFoil tier={holoTier} radius={10} intensity={owned ? 1 : 0.55} style={{ zIndex: 1 }} />}
        <div style={{ position: "absolute", inset: 0, zIndex: 2, background: "linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.88) 100%)" }} />
        <div style={{ position: "absolute", top: 7, left: 8, right: 8, zIndex: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 8, color: holoColor || typeColor, fontWeight: 900, letterSpacing: 1, textShadow: "0 1px 3px rgba(0,0,0,0.9)", fontFamily: SD.fontDisplay }}>{card.type?.toUpperCase()}{card.level ? ` L${card.level}` : ""}</div>
          {!owned && <span style={{ fontSize: 7, color: "#ff6666", border: "1px solid #ff666688", borderRadius: 4, padding: "1px 4px", background: "rgba(0,0,0,0.6)" }}>UNOWNED</span>}
        </div>
        <div style={{ position: "absolute", top: 18, left: 8, right: 8, zIndex: 3, fontSize: 10, fontStyle: "italic", color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.95)", fontFamily: SD.fontDisplay, lineHeight: 1.3 }}>{card.name}</div>
        {holoLabel && <div style={{ position: "absolute", top: 32, left: 8, zIndex: 3, fontSize: 7, color: holoColor, fontWeight: 900, letterSpacing: 0.5, textShadow: `0 0 4px ${holoColor}` }}>{holoLabel}</div>}
        <div style={{ position: "absolute", bottom: 7, left: 8, right: 8, zIndex: 3 }}>
          {card.type === "Escendant" && (<div style={{ display: "flex", justifyContent: "space-between" }}><StatChip kind="lyf" value={card.lyf} color="#88ccff" size={11} fontSize={13} bare /><StatChip kind="lyt" value={card.lyt} color="#ff8888" size={11} fontSize={13} bare /></div>)}
          {card.khaoCost > 0 && <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: SD.goldBright, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}><StatGlyph kind="khao" size={8} color={SD.goldBright} />{card.khaoCost} Khao</div>}
          {card.animaCost > 0 && <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: "#f87171", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}><StatGlyph kind="anima" size={8} color="#f87171" />{card.animaCost} Anima</div>}
        </div>
        {count > 0 && <div style={{ position: "absolute", top: 6, right: 6, zIndex: 4, width: 20, height: 20, borderRadius: "50%", background: holoColor || typeColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: "#000" }}>{count}</div>}
      </div>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button aria-label="Remove one copy" onClick={e => { e.stopPropagation(); onRemove(); }} disabled={count === 0} style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${SD.void4}`, background: "transparent", color: count === 0 ? SD.void4 : SD.red, cursor: count === 0 ? "not-allowed" : "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SD.fontDisplay }}>−</button>
        <div style={{ fontSize: 11, color: count > 0 ? (holoColor || typeColor) : SD.starDim, fontWeight: 900, minWidth: 28, textAlign: "center", fontFamily: SD.fontDisplay }}>{count}/{maxAllowed}</div>
        <button aria-label="Add one copy" onClick={e => { e.stopPropagation(); onAdd(); }} disabled={atMax} style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${atMax ? SD.void4 : (holoColor || typeColor)}`, background: atMax ? "transparent" : `${holoColor || typeColor}22`, color: atMax ? SD.void4 : (holoColor || typeColor), cursor: atMax ? "not-allowed" : "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SD.fontDisplay }}>+</button>
        {/* T21 multi-mark — appears only when this card HAS plain dupes.
            Marking a card means "all its plain dupes"; foils never bulk. */}
        {scrap && onToggleMark && Math.max(0, (record?.count || 0) - 1) > 0 && (
          <button title={marked ? "Unmark for scrap" : "Mark plain dupes for scrap"}
            onClick={e => { e.stopPropagation(); onToggleMark(card.id); }} style={{
            width: 28, height: 28, borderRadius: "50%",
            border: `1px solid ${marked ? "#ff9c4a" : SD.void4}`,
            background: marked ? "#ff9c4a22" : "transparent",
            color: marked ? "#ff9c4a" : SD.starDim, cursor: "pointer",
            fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center",
          }}>♻</button>
        )}
      </div>
      {onSelectHolo && (
        <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          {/* TRIGGER — one swatch, always visible. Shows the current foil (or a
              plain ∅ pip). Tap to reveal the palette of available marquees. */}
          <button onClick={e => { e.stopPropagation(); setPaletteOpen(o => !o); }}
            title={holoLabel || "Choose foil"}
            style={{
              display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
              padding: "3px 9px", borderRadius: 12,
              background: holoColor ? `${holoColor}1e` : "transparent",
              border: `1px solid ${holoColor || SD.void4}`,
              transition: "all 0.15s",
            }}>
            <span style={{
              width: 13, height: 13, borderRadius: "50%", flexShrink: 0,
              background: holoColor || `linear-gradient(135deg, ${SD.void2}, ${SD.void3})`,
              boxShadow: holoColor ? `0 0 6px ${holoColor}` : "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 8, color: SD.starDim, lineHeight: 1,
            }}>{holoColor ? "" : "∅"}</span>
            <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.5, fontFamily: SD.fontDisplay, color: holoColor || SD.starDim }}>
              {holoLabel || "No Holo"}
            </span>
            <span style={{ fontSize: 7, color: holoColor || SD.starDim, transform: paletteOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>▾</span>
          </button>

          {/* PALETTE — the popout. Two faces:
              (A) TIER swatches: plain ∅ + every available marquee. Each
                  marquee shows a NUMBER badge when you own it from 2+ sources.
                  Tap a single-source (or Free Play) tier → applies instantly.
                  Tap a numbered tier → drops into the SIGIL sub-picker (B).
              (B) SIGIL sub-picker: only the sources you own for that tier,
                  each a tappable <SourceSigil>. Pick one → adds that exact
                  {tier, source} instance. ← back returns to the swatches. */}
          {paletteOpen && sigilTier === null && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 5, justifyContent: "center",
              maxWidth: CARD_W + 6, padding: "7px 8px",
              background: `linear-gradient(145deg, ${SD.void2}f5, ${SD.void1}f8)`,
              border: `1px solid ${SD.void4}`, borderRadius: 10,
              boxShadow: "0 6px 18px rgba(0,0,0,0.55)",
            }}>
              {/* No-Holo pip */}
              <button onClick={e => { e.stopPropagation(); onSelectHolo(card.id, null); setPaletteOpen(false); }} title="No Holo"
                style={{
                  width: 17, height: 17, borderRadius: "50%", cursor: "pointer", padding: 0,
                  background: `linear-gradient(135deg, ${SD.void2}, ${SD.void3})`,
                  border: !holoTier ? `2px solid ${SD.vellum || "#e8e0d0"}` : `1px solid ${SD.void4}`,
                  boxShadow: !holoTier ? `0 0 8px ${SD.starDim}` : "none",
                  transform: !holoTier ? "scale(1.15)" : "scale(1)", transition: "all 0.12s",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: SD.starDim, lineHeight: 1,
                }}>∅</button>
              {(availableTiers || []).map(t => {
                const info = HOLO_TIER_INFO[t];
                if (!info) return null;
                const active = t === holoTier;
                const srcs = sourcesForTier(t);          // distinct sources owned in this tier
                const multi = srcs.length >= 2;          // → numbered swatch + sub-picker
                return (
                  <div key={t} style={{ position: "relative", width: 17, height: 17 }}>
                    <button title={info.label} onClick={e => {
                      e.stopPropagation();
                      if (multi && onSelectVariant) { setSigilTier(t); return; }   // open sub-picker
                      // single source (or Free Play): apply directly. If exactly
                      // one source is known, pin that provenance to the instance.
                      if (onSelectVariant && srcs.length === 1) onSelectVariant(card.id, t, srcs[0]);
                      else onSelectHolo(card.id, t);
                      setPaletteOpen(false);
                    }}
                      style={{
                        width: 17, height: 17, borderRadius: "50%", cursor: "pointer", padding: 0,
                        background: info.color,
                        border: active ? `2px solid ${SD.vellum || "#e8e0d0"}` : `1px solid ${info.color}88`,
                        boxShadow: active ? `0 0 8px ${info.color}` : "none",
                        transform: active ? "scale(1.15)" : "scale(1)", transition: "all 0.12s",
                      }} />
                    {multi && (
                      <span style={{
                        position: "absolute", top: -5, right: -5, zIndex: 2,
                        minWidth: 13, height: 13, padding: "0 2px", borderRadius: 7,
                        background: SD.void1, border: `1px solid ${info.color}`,
                        color: info.color, fontSize: 8, fontWeight: 900, lineHeight: "12px",
                        textAlign: "center", fontFamily: SD.fontDisplay, pointerEvents: "none",
                        boxShadow: `0 0 5px ${info.color}88`,
                      }}>{srcs.length}</span>
                    )}
                  </div>
                );
              })}
              {(!availableTiers || availableTiers.length === 0) && (
                <span style={{ fontSize: 8, color: SD.starDim, fontStyle: "italic", alignSelf: "center", padding: "0 2px" }}>
                  No foils owned yet
                </span>
              )}
            </div>
          )}

          {/* (B) SIGIL SUB-PICKER — choose WHICH source's copy goes in the deck.
              Only renders the sources actually owned for the chosen tier. This
              is where a Solrend-from-Shadow-Fox and a Solrend-from-Vault become
              two distinct, playable instances of the same card. */}
          {paletteOpen && sigilTier !== null && (
            <div style={{
              display: "flex", flexDirection: "column", gap: 7, alignItems: "stretch",
              maxWidth: CARD_W + 30, padding: "8px 9px",
              background: `linear-gradient(145deg, ${SD.void2}f8, ${SD.void1}fc)`,
              border: `1px solid ${HOLO_COLORS[sigilTier] || SD.void4}`, borderRadius: 10,
              boxShadow: "0 8px 22px rgba(0,0,0,0.6)",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <button onClick={e => { e.stopPropagation(); setSigilTier(null); }}
                  style={{ background: "transparent", border: "none", color: SD.starDim, cursor: "pointer", fontSize: 11, fontFamily: SD.fontDisplay, padding: 0 }}>← back</button>
                <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: 0.5, color: HOLO_COLORS[sigilTier], fontFamily: SD.fontDisplay }}>
                  {(HOLO_LABELS[sigilTier] || "").toUpperCase()} · CHOOSE SOURCE
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                {sourcesForTier(sigilTier).map(src => (
                  <button key={src} title={getSourceLabel(src)} onClick={e => {
                    e.stopPropagation();
                    if (onSelectVariant) onSelectVariant(card.id, sigilTier, src);
                    setSigilTier(null); setPaletteOpen(false);
                  }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                      cursor: "pointer", padding: "6px 8px", borderRadius: 8,
                      background: `${SD.void3}66`, border: `1px solid ${SD.void4}`,
                      minWidth: 58, transition: "all 0.12s",
                    }}>
                    <SourceSigil source={src} size={40} />
                    <span style={{ fontSize: 7, color: SD.starSilver, fontFamily: SD.fontDisplay, lineHeight: 1.2, textAlign: "center", maxWidth: 64 }}>
                      {getSourceLabel(src)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {lightbox && <CardLightbox card={card} holoTier={holoTier} cardSources={cardSources} sourceTiers={sourceTiers} record={record} scrap={scrap} onClose={() => setLightbox(false)} />}
    </div>
  );
}
function SaveModal({ onSave, onClose, existing }) {
  const [name, setName] = useState("");
  useModalKeys(onClose);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(4px)" }}>
      <div style={{ position: "relative", background: OBSIDIAN_PANEL, border: `1px solid ${GM.inkGold}`, borderRadius: 14, padding: "30px 36px", minWidth: 300, boxShadow: SHADOW_FLOAT, fontFamily: GM.font }}>
        <TerrorFrame />
        <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: SD.gold, letterSpacing: 3, marginBottom: 22, textAlign: "center" }}>✦ Name Your Grimoire ✦</div>
        <input autoFocus placeholder="Enter a name..." value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && name.trim() && onSave(name.trim())} style={{ width: "100%", background: SD.void1, border: `1px solid ${SD.void4}`, borderRadius: 6, color: SD.starSilver, padding: "11px 14px", fontFamily: SD.fontDisplay, fontSize: 14, boxSizing: "border-box", marginBottom: 14, outline: "none" }} />
        {existing?.includes(name.trim()) && <div style={{ fontSize: 11, color: SD.red, marginBottom: 12, fontStyle: "italic" }}>⚠ Name exists — saving will overwrite.</div>}
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${SD.void4}`, color: SD.starDim, padding: "10px 22px", borderRadius: 6, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 12 }}>Cancel</button>
          <button onClick={() => name.trim() && onSave(name.trim())} disabled={!name.trim()} style={{ background: name.trim() ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${name.trim() ? SD.gold : SD.void4}`, color: name.trim() ? SD.gold : SD.void4, padding: "10px 22px", borderRadius: 6, cursor: name.trim() ? "pointer" : "not-allowed", fontFamily: SD.fontDisplay, fontSize: 12, fontStyle: "italic" }}>✦ Save</button>
        </div>
      </div>
    </div>
  );
}

function LoadModal({ grimoires, onLoad, onDelete, onClose }) {
  useModalKeys(onClose);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(4px)" }}>
      {/* Shell holds the house frame; content scrolls in the inner region
          (TerrorFrame law: frame on a non-scrolling shell — SoulModal pattern) */}
      <div style={{ position: "relative", background: OBSIDIAN_PANEL, border: `1px solid ${GM.pageBorder}`, borderRadius: 14, minWidth: 320, maxWidth: 380, maxHeight: "80vh", boxShadow: SHADOW_FLOAT, fontFamily: GM.font, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <TerrorFrame />
        <div style={{ overflowY: "auto", padding: "28px 32px", minHeight: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: SD.purpleLight, letterSpacing: 3, marginBottom: 18, textAlign: "center" }}>✦ Your Grimoires ✦</div>
        {Object.keys(grimoires).length === 0 && <div style={{ color: SD.starDim, fontStyle: "italic", fontSize: 13, textAlign: "center", marginBottom: 18 }}>No saved Grimoires yet.</div>}
        {Object.keys(grimoires).map(name => {
          const deck = grimoires[name];
          const gCount = Object.values(deck.grimoire || {}).reduce((a, b) => a + b, 0);
          const lCount = Object.values(deck.luxmoire || {}).reduce((a, b) => a + b, 0);
          return (
            <div key={name} style={{ background: SD.void1, border: `1px solid ${SD.void4}`, borderRadius: 8, padding: "12px 16px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic", color: SD.starSilver, fontFamily: SD.fontDisplay }}>{name}</div>
                <div style={{ fontSize: 11, color: SD.starDim, marginTop: 3 }}>Grimoire: {gCount} • Luxmoire: {lCount}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => onLoad(name)} style={{ background: `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})`, border: `1px solid ${SD.gold}`, color: SD.gold, padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 11, fontStyle: "italic" }}>Load</button>
                <button aria-label="Delete this set" onClick={() => onDelete(name)} style={{ background: "transparent", border: `1px solid ${SD.red}`, color: SD.red, padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 11 }}>✕</button>
              </div>
            </div>
          );
        })}
        <Divider />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${SD.void4}`, color: SD.starDim, padding: "10px 28px", borderRadius: 6, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 13 }}>Close</button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// QUICK BUILD WIZARD — Full step-by-step guided deck creation
// ─────────────────────────────────────────────────────────────

const RECOMMENDED_TOMES = [
  "tome_khao_garden", "tome_gauntlets_azka", "tome_wall_of_light",
  "tome_nothing_left", "tome_blood_in_blood_out", "tome_not_here",
  "tome_devils_advocate", "tome_seal_of_omnis", "tome_seal_of_nihil",
  "tome_soulcerers_seal",
];

const TONE_CATEGORIES = ["Construction","Treasure","Nihil","Omnia","Ancient Sorcery","Ritual","Contract","Shadow Gambling"];

const AFFINITY_INFO = [
  { id: "aff_holy",   name: "Holy — A'aeternis",    icon: "✨", title: "A'aeternis",   desc: "Nullify any 1 attack. The shield of the worthy." },
  { id: "aff_awoken", name: "Enlightened — V'igiluz", icon: "👁", title: "V'igiluz",   desc: "Nullify 1 Affinity. Counter the divine." },
  { id: "aff_evil",   name: "Evil — A'oeternis",   icon: "🌑", title: "A'oeternis",  desc: "Send 1 card to Necropolis. The purge." },
  { id: "aff_jynxi",  name: "Jester — J'ynxi",     icon: "🃏", title: "J'ynxi",      desc: "Retrieve Shadow Gamblings. Fortune's fool." },
];

function StepIndicator({ steps, current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, background: done ? SD.purpleLight : active ? SD.gold : SD.void3, color: done || active ? SD.void0 : SD.void4, border: `2px solid ${done ? SD.purpleLight : active ? SD.gold : SD.void4}`, transition: "all 0.3s" }}>
                {done ? "✓" : i + 1}
              </div>
              <div style={{ fontSize: 8, color: active ? SD.gold : done ? SD.purpleLight : SD.void4, letterSpacing: 1, whiteSpace: "nowrap" }}>{s}</div>
            </div>
            {i < steps.length - 1 && <div style={{ width: 20, height: 2, background: done ? SD.purpleLight : SD.void3, margin: "0 2px", marginBottom: 16, transition: "all 0.3s" }} />}
          </div>
        );
      })}
    </div>
  );
}

// CardStepper — full card tile with +/- controls for QuickBuild
function CountStepper({ label, sublabel, value, min, max, onChange, color, recommended, cardId, holoTier = null }) {
  const CARD_W = 120;
  const CARD_H = 168;
  const imgUrl = cardId ? cardUrl(cardId) : null; // CDN law — one domain, one file
  const typeColor = color || SD.purpleLight;
  const holoColor = holoTier ? HOLO_COLORS[holoTier] : null;

  // If no cardId, fall back to text style
  if (!cardId) return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, background: SD.void2, border: `1px solid ${recommended ? SD.gold + "66" : SD.void4}`, borderRadius: 8, padding: "12px 16px", position: "relative" }}>
      {recommended && <div style={{ position: "absolute", top: -8, right: 10, fontSize: 9, color: SD.gold, background: SD.void2, padding: "1px 6px", border: `1px solid ${SD.gold}44`, borderRadius: 6 }}>⭐</div>}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: typeColor, fontFamily: SD.fontDisplay }}>{label}</div>
        {sublabel && <div style={{ fontSize: 10, color: SD.starSilver, marginTop: 2 }}>{sublabel}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button aria-label="Remove one" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${value <= min ? SD.void4 : SD.red}`, background: "transparent", color: value <= min ? SD.void4 : SD.red, fontSize: 18, cursor: value <= min ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
        <span style={{ fontSize: 18, fontWeight: 900, color: value > 0 ? SD.gold : SD.void4, minWidth: 24, textAlign: "center", fontFamily: SD.fontDisplay }}>{value}</span>
        <button aria-label="Add one" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${value >= max ? SD.void4 : SD.purpleLight}`, background: "transparent", color: value >= max ? SD.void4 : SD.purpleLight, fontSize: 18, cursor: value >= max ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
      </div>
    </div>
  );

  // Full card tile with image
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginBottom: 4 }}>
      {/* Card tile */}
      <div style={{ position: "relative", width: CARD_W, height: CARD_H, borderRadius: 10, overflow: "hidden", border: `1px solid ${holoColor ? holoColor : (value > 0 ? typeColor + "66" : typeColor + "22")}`, boxShadow: holoColor && value > 0 ? `0 0 16px ${holoColor}66` : (recommended && value > 0 ? `0 0 8px ${SD.gold}33` : "none"), transition: "all 0.2s", flexShrink: 0 }}>
        {/* Art */}
        <img src={imgUrl} alt={label} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
        {/* Gradient overlay */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.88) 100%)" }} />
        {/* Recommended badge */}
        {recommended && <div style={{ position: "absolute", top: 6, left: 6, fontSize: 8, color: SD.gold, background: "rgba(0,0,0,0.8)", padding: "2px 5px", border: `1px solid ${SD.gold}66`, borderRadius: 4 }}>⭐ REC</div>}
        {/* Count badge */}
        {value > 0 && <div style={{ position: "absolute", top: 6, right: 6, width: 22, height: 22, borderRadius: "50%", background: typeColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#000" }}>{value}</div>}
        {/* Card name */}
        <div style={{ position: "absolute", bottom: 28, left: 6, right: 6, fontSize: 9, fontStyle: "italic", color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.95)", lineHeight: 1.3, fontFamily: SD.fontDisplay }}>{label}</div>
        {/* Sublabel */}
        {sublabel && <div style={{ position: "absolute", bottom: 18, left: 6, right: 6, fontSize: 7, color: SD.starSilver, textShadow: "0 1px 3px rgba(0,0,0,0.9)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{sublabel}</div>}
        <div style={{ position: "absolute", bottom: 6, left: 6, fontSize: 8, color: typeColor, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>max {max}</div>
      </div>
      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button aria-label="Remove one" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${value <= min ? SD.void4 : SD.red}`, background: "transparent", color: value <= min ? SD.void4 : SD.red, fontSize: 16, cursor: value <= min ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
        <span style={{ fontSize: 16, fontWeight: 900, color: value > 0 ? SD.gold : SD.void4, minWidth: 20, textAlign: "center", fontFamily: SD.fontDisplay }}>{value}</span>
        <button aria-label="Add one" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${value >= max ? SD.void4 : SD.purpleLight}`, background: "transparent", color: value >= max ? SD.void4 : SD.purpleLight, fontSize: 16, cursor: value >= max ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
      </div>
    </div>
  );
}

function QuickBuild({ onFinish, onBack }) {
  const STEPS = ["Line","Beings","Impera","Tomes","U'hlti","Affinity","Prophecy","Review"];
  const [step, setStep] = useState(0);
  const [selectedLine, setSelectedLine] = useState(null);
  useModalKeys(onBack);

  // Beings
  const [bebCount, setBebCount] = useState(3);
  const [champCount, setChampCount] = useState(2);
  const [lordCount, setLordCount] = useState(1);
  const [supportBeings, setSupportBeings] = useState({}); // {cardId: count}

  // Impera
  const [primaryImpera, setPrimaryImpera] = useState({ l1: 3, l2: 2, l3: 1 });
  const [supportImpera, setSupportImpera] = useState({});

  // Tomes
  const [tomes, setTomes] = useState({});

  // U'hlti, Affinity, Prophecy
  const [includeUhlti, setIncludeUhlti] = useState(true);
  const [selectedAffinity, setSelectedAffinity] = useState(null);
  const [selectedProphecy, setSelectedProphecy] = useState(null);

  // When line is selected, init recommended tomes
  useEffect(() => {
    if (selectedLine) {
      const base = {};
      RECOMMENDED_TOMES.forEach(id => {
        const card = TOMES.find(c => c.id === id);
        if (card) base[id] = card.id === "tome_khao_garden" ? 3 : 1;
      });
      setTomes(base);
    }
  }, [selectedLine]);

  const line = selectedLine;

  // Same-Ilk support lines
  const supportLines = line ? ESCENDANT_LINES.filter(l => l.ilk === line.ilk && l.id !== line.id) : [];

  // Build final deck
  const buildAndFinish = () => {
    const grimoire = { ...tomes };
    // Primary beings
    if (bebCount > 0) grimoire[line.beings[0]] = bebCount;
    if (champCount > 0) grimoire[line.beings[1]] = champCount;
    if (lordCount > 0) grimoire[line.beings[2]] = lordCount;
    // Support beings
    Object.entries(supportBeings).forEach(([id, qty]) => { if (qty > 0) grimoire[id] = qty; });
    // (THE LANES: U'hlti + Affinity are Luxmoire cards — added below)

    // Fill to 35
    let gCount = Object.values(grimoire).reduce((a, b) => a + b, 0);
    const fillTomes = ["tome_khao_garden","tome_bhodi_tree","tome_khao_duhmdumn_beb","tome_wall_of_light","tome_nothing_left"];
    let fi = 0;
    while (gCount < 35 && fi < fillTomes.length) {
      const tid = fillTomes[fi];
      const card = TOMES.find(c => c.id === tid);
      const max = card?.deckLimit || 3;
      const current = grimoire[tid] || 0;
      if (current < max) { grimoire[tid] = current + 1; gCount++; }
      else fi++;
    }

    // Luxmoire
    const luxmoire = {};
    if (primaryImpera.l1 > 0) luxmoire[line.impera[0]] = primaryImpera.l1;
    if (primaryImpera.l2 > 0) luxmoire[line.impera[1]] = primaryImpera.l2;
    if (primaryImpera.l3 > 0) luxmoire[line.impera[2]] = primaryImpera.l3;
    Object.entries(supportImpera).forEach(([id, qty]) => { if (qty > 0) luxmoire[id] = qty; });
    // ── THE LANES: U'hlti + Affinity live in the Luxmoire ──
    const uhltiId = `u_${line.id}_lord`;
    if (includeUhlti && UHLTIS.find(u => u.id === uhltiId)) luxmoire[uhltiId] = 1;
    if (selectedAffinity) luxmoire[selectedAffinity] = 1;
    // Fill Luxmoire to 10
    let lCount = Object.values(luxmoire).reduce((a, b) => a + b, 0);
    while (lCount < 10) { luxmoire[line.impera[0]] = (luxmoire[line.impera[0]] || 0) + 1; lCount++; }

    // Prophecy goes to deck metadata
    onFinish(grimoire, luxmoire, `${line.name} Deck`, selectedProphecy);
  };

  const gTotal = (() => {
    let n = Object.values(tomes).reduce((a,b)=>a+b,0) + bebCount + champCount + lordCount;
    Object.values(supportBeings).forEach(v => n += v);
    if (includeUhlti) n += 1;
    if (selectedAffinity) n += 1;
    return n;
  })();
  const lTotal = primaryImpera.l1 + primaryImpera.l2 + primaryImpera.l3 + Object.values(supportImpera).reduce((a,b)=>a+b,0);

  const NavButtons = ({ onNext, nextLabel = "Next →", nextDisabled = false, showBack = true }) => (
    <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
      {showBack && <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: "13px", background: "transparent", border: `1px solid ${SD.void4}`, borderRadius: 10, color: SD.starDim, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 13 }}>← Back</button>}
      <button onClick={onNext} disabled={nextDisabled} style={{ flex: 2, padding: "13px", background: nextDisabled ? "transparent" : `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})`, border: `1px solid ${nextDisabled ? SD.void4 : SD.gold}`, borderRadius: 10, color: nextDisabled ? SD.void4 : SD.gold, cursor: nextDisabled ? "not-allowed" : "pointer", fontFamily: SD.fontDisplay, fontSize: 14, fontWeight: 900, fontStyle: "italic", boxShadow: nextDisabled ? "none" : shimmerBorder(SD.gold) }}>
        {nextLabel}
      </button>
    </div>
  );

  // ── STEP 0: PICK LINE ───────────────────────────────────────
  if (step === 0) return (
    <div>
      <StepIndicator steps={STEPS} current={0} />
      <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 16, fontStyle: "italic" }}>
        Choose your <span style={{ color: SD.gold }}>Escendant line</span> — the beings you'll train and bring to battle.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "50vh", overflowY: "auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-start" }}>
        {ESCENDANT_LINES.map(l => {
          const lineImgUrl = cardUrl(l.beings[0]); // CDN law — one domain, one file
          const isSelected = selectedLine?.id === l.id;
          return (
          <div key={l.id} onClick={() => setSelectedLine(l)} style={{ position: "relative", width: 120, borderRadius: 10, overflow: "hidden", border: `2px solid ${isSelected ? l.color : l.color + "44"}`, cursor: "pointer", boxShadow: isSelected ? `0 0 18px ${l.color}66` : "none", transition: "all 0.2s", flexShrink: 0 }}>
            {/* Line preview art */}
            <img src={lineImgUrl} alt={l.name} style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} onError={e => { e.target.style.display = "none"; e.target.parentNode.style.background = SD.void2; }} />
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 120, background: "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.85) 100%)" }} />
            {isSelected && <div style={{ position: "absolute", top: 6, right: 6, width: 16, height: 16, borderRadius: "50%", background: l.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>✓</div>}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "6px 8px", background: "rgba(0,0,0,0.7)" }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: isSelected ? l.color : SD.starWhite, fontFamily: SD.fontDisplay, lineHeight: 1.2 }}>{l.name}</div>
              <div style={{ fontSize: 8, color: l.color, letterSpacing: 1 }}>{l.ilk}</div>
            </div>
          </div>
          );
        })}
        </div>
      </div>
      <NavButtons showBack={false} onNext={() => setStep(1)} nextDisabled={!selectedLine} nextLabel={selectedLine ? `✦ Build ${selectedLine.name} →` : "Select a line first"} />
    </div>
  );

  // ── STEP 1: BEINGS ─────────────────────────────────────────
  if (step === 1) {
    const BebCard = ESCENDANTS.find(e => e.id === line.beings[0]);
    const ChampCard = ESCENDANTS.find(e => e.id === line.beings[1]);
    const LordCard = ESCENDANTS.find(e => e.id === line.beings[2]);
    return (
      <div>
        <StepIndicator steps={STEPS} current={1} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 22 }}>{line.icon}</span>
          <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: line.color, fontFamily: SD.fontDisplay }}>{line.name} — Your Primary Line</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 12 }}>
        <CountStepper label={BebCard?.name} sublabel="Level 1 — Your starter." value={bebCount} min={1} max={5} onChange={setBebCount} color={line.color} recommended cardId={line.beings[0]} />
        <CountStepper label={ChampCard?.name} sublabel="Level 2 — Mid-game power." value={champCount} min={1} max={4} onChange={setChampCount} color={line.color} recommended cardId={line.beings[1]} />
        <CountStepper label={LordCard?.name} sublabel="Level 3 — Your Lord." value={lordCount} min={1} max={3} onChange={setLordCount} color={line.color} recommended cardId={line.beings[2]} />
        </div>

        {supportLines.length > 0 && (
          <>
            <div style={{ fontSize: 10, letterSpacing: 2, color: SD.starDim, margin: "16px 0 10px" }}>SUPPORT BEINGS — Same Ilk ({line.ilk})</div>
            {supportLines.map(sl => (
              <div key={sl.id}>
                <div style={{ fontSize: 11, color: sl.color, fontWeight: 900, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>{sl.icon}</span>{sl.name}
                </div>
                {sl.beings.map((bid, li) => {
                  const bc = ESCENDANTS.find(e => e.id === bid);
                  const lvl = ["L1","L2","L3"][li];
                  const maxArr = [5,4,3];
                  return (
                    <CountStepper key={bid} label={bc?.name || bid} sublabel={`${lvl} Support`} value={supportBeings[bid] || 0} min={0} max={maxArr[li]} onChange={v => setSupportBeings(p => ({...p,[bid]:v}))} color={sl.color} cardId={bid} />
                  );
                })}
              </div>
            ))}
          </>
        )}
        <div style={{ fontSize: 11, color: SD.starDim, fontStyle: "italic", marginTop: 8 }}>Total Escendants: <span style={{ color: SD.gold, fontWeight: 900 }}>{bebCount + champCount + lordCount + Object.values(supportBeings).reduce((a,b)=>a+b,0)}</span></div>
        <NavButtons onNext={() => setStep(2)} />
      </div>
    );
  }

  // ── STEP 2: IMPERA ────────────────────────────────────────
  if (step === 2) {
    const p1 = ALL_LUXMOIRE_CARDS.find(c => c.id === line.impera[0]);
    const p2 = ALL_LUXMOIRE_CARDS.find(c => c.id === line.impera[1]);
    const p3 = ALL_LUXMOIRE_CARDS.find(c => c.id === line.impera[2]);
    // Support impera = impera matching support being lines
    const addedSupportLineIds = supportLines.filter(sl => sl.beings.some(bid => (supportBeings[bid] || 0) > 0));
    return (
      <div>
        <StepIndicator steps={STEPS} current={2} />
        <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 14, fontStyle: "italic" }}>
          Impera are your beings' special abilities. Match them to your Escendant line.
        </div>
        <div style={{ fontSize: 11, color: line.color, fontWeight: 900, marginBottom: 10 }}>{line.icon} {line.name} Impera</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 12 }}>
        <CountStepper label={p1?.name || "L1 Impera"} sublabel="Level 1 Impera" value={primaryImpera.l1} min={0} max={5} onChange={v => setPrimaryImpera(p=>({...p,l1:v}))} color={line.color} recommended cardId={line.impera[0]} />
        <CountStepper label={p2?.name || "L2 Impera"} sublabel="Level 2 Impera" value={primaryImpera.l2} min={0} max={4} onChange={v => setPrimaryImpera(p=>({...p,l2:v}))} color={line.color} recommended cardId={line.impera[1]} />
        <CountStepper label={p3?.name || "L3 Impera"} sublabel="Level 3 Impera" value={primaryImpera.l3} min={0} max={3} onChange={v => setPrimaryImpera(p=>({...p,l3:v}))} color={line.color} recommended cardId={line.impera[2]} />
        </div>

        {addedSupportLineIds.map(sl => (
          <div key={sl.id}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: SD.starDim, margin: "14px 0 8px" }}>SUPPORT — {sl.name} Impera</div>
            {sl.impera.map((pid, li) => {
              const pc = ALL_LUXMOIRE_CARDS.find(c => c.id === pid);
              const maxArr = [5,4,3];
              return (
                <CountStepper key={pid} label={pc?.name || pid} sublabel={`L${li+1} Impera`} value={supportImpera[pid] || 0} min={0} max={maxArr[li]} onChange={v => setSupportImpera(p=>({...p,[pid]:v}))} color={sl.color} cardId={pid} />
              );
            })}
          </div>
        ))}
        <div style={{ fontSize: 11, color: SD.starDim, fontStyle: "italic", marginTop: 8 }}>
          Luxmoire total: <span style={{ color: lTotal >= 10 ? SD.purpleLight : SD.gold, fontWeight: 900 }}>{lTotal}/10+ needed</span>
        </div>
        <NavButtons onNext={() => setStep(3)} />
      </div>
    );
  }

  // ── STEP 3: TOMES ──────────────────────────────────────────
  if (step === 3) {
    const tomesByCategory = {};
    TONE_CATEGORIES.forEach(cat => {
      tomesByCategory[cat] = TOMES.filter(t => t.ilk === cat);
    });
    return (
      <div>
        <StepIndicator steps={STEPS} current={3} />
        <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 8, fontStyle: "italic" }}>
          Tomes are your support cards. <span style={{ color: SD.gold }}>⭐ Starred</span> ones are recommended for most decks.
        </div>
        <div style={{ fontSize: 11, color: SD.starDim, marginBottom: 14 }}>
          Grimoire so far: <span style={{ color: gTotal >= 35 ? SD.purpleLight : SD.gold, fontWeight: 900 }}>{gTotal}/35</span>
        </div>
        <div style={{ maxHeight: "52vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
          {TONE_CATEGORIES.map(cat => (
            <div key={cat}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: SD.starDim, marginBottom: 8, borderBottom: `1px solid ${SD.void4}`, paddingBottom: 4 }}>{cat.toUpperCase()}</div>
              {tomesByCategory[cat].map(t => (
                <CountStepper key={t.id} label={t.name} sublabel={t.effectTag || ""} value={tomes[t.id] || 0} min={0} max={t.deckLimit || 1} onChange={v => setTomes(p => ({...p,[t.id]:v}))} color={TYPE_COLORS_SD["Tome"] || SD.blue} recommended={RECOMMENDED_TOMES.includes(t.id)} cardId={t.id} />
              ))}
            </div>
          ))}
        </div>
        <NavButtons onNext={() => setStep(4)} />
      </div>
    );
  }

  // ── STEP 4: U'HLTI ─────────────────────────────────────────
  if (step === 4) {
    const uhltiId = `u_${line.id}_lord`;
    const uhltiCard = UHLTIS.find(u => u.id === uhltiId);
    return (
      <div>
        <StepIndicator steps={STEPS} current={4} />
        <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 16, fontStyle: "italic" }}>
          A U'hlti is permanently attached to your Natural Level 3 being. Powerful and exclusive.
        </div>
        {uhltiCard ? (
          <div onClick={() => setIncludeUhlti(v => !v)} style={{ background: includeUhlti ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${includeUhlti ? SD.purpleLight : SD.void4}`, borderRadius: 12, padding: "18px 20px", cursor: "pointer", boxShadow: includeUhlti ? shimmerBorder(SD.purple) : "none", transition: "all 0.2s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 10, color: SD.purpleLight, letterSpacing: 2, fontWeight: 900 }}>U'HLTI • LEVEL 3</div>
                <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: includeUhlti ? SD.purpleLight : SD.starSilver, fontFamily: SD.fontDisplay }}>{uhltiCard.name}</div>
                <div style={{ fontSize: 11, color: SD.starDim, marginTop: 4 }}>Owner: {line.name} Lord</div>
              </div>
              <div style={{ fontSize: 24, color: includeUhlti ? SD.purpleLight : SD.void4 }}>{includeUhlti ? "✦" : "◇"}</div>
            </div>
            {uhltiCard.effect && (
              <div style={{ fontSize: 12, color: SD.starDim, lineHeight: 1.6, borderLeft: `2px solid ${SD.purpleDim}`, paddingLeft: 12 }}>
                {uhltiCard.effectTag && <span style={{ color: SD.gold, fontWeight: 900 }}>{uhltiCard.effectTag}: </span>}
                {uhltiCard.effect}
              </div>
            )}
            <div style={{ fontSize: 11, color: includeUhlti ? SD.purpleLight : SD.void4, marginTop: 10, fontStyle: "italic", textAlign: "center" }}>
              {includeUhlti ? "✦ Included — tap to remove" : "◇ Not included — tap to add"}
            </div>
          </div>
        ) : (
          <div style={{ color: SD.starDim, fontStyle: "italic", fontSize: 13, textAlign: "center", padding: "20px 0" }}>No U'hlti found for {line.name}. You can add one manually in Classic Builder.</div>
        )}
        <NavButtons onNext={() => setStep(5)} />
      </div>
    );
  }

  // ── STEP 5: AFFINITY ────────────────────────────────────────
  if (step === 5) {
    return (
      <div>
        <StepIndicator steps={STEPS} current={5} />
        <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 8, fontStyle: "italic" }}>
          Affinities grant your Supreme Being a divine title and a powerful ability. Only 1 per Grimoire — or none.
        </div>
        <div style={{ fontSize: 10, color: GM.vellumFaint, marginBottom: 16, lineHeight: 1.6 }}>
          Supreme Being = Natural L3 + all 3 Impera + U'hlti + Affinity. Required for most Prophecies.
        </div>
        <div onClick={() => setSelectedAffinity(null)} style={{ background: selectedAffinity === null ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${selectedAffinity === null ? SD.starDim : SD.void4}`, borderRadius: 10, padding: "12px 16px", cursor: "pointer", marginBottom: 8, transition: "all 0.2s" }}>
          <div style={{ fontSize: 14, fontStyle: "italic", color: selectedAffinity === null ? SD.starSilver : SD.void4 }}>◇ No Affinity — skip this step</div>
        </div>
        {AFFINITY_INFO.map(aff => (
          <div key={aff.id} onClick={() => setSelectedAffinity(aff.id)} style={{ background: selectedAffinity === aff.id ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${selectedAffinity === aff.id ? SD.gold : SD.void4}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", marginBottom: 8, boxShadow: selectedAffinity === aff.id ? shimmerBorder(SD.gold) : "none", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>{aff.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic", color: selectedAffinity === aff.id ? SD.gold : SD.starSilver, fontFamily: SD.fontDisplay }}>{aff.name}</div>
                <div style={{ fontSize: 10, color: SD.gold, letterSpacing: 1 }}>Supreme Title: {aff.title}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: SD.starDim, paddingLeft: 32 }}>{aff.desc}</div>
          </div>
        ))}
        <NavButtons onNext={() => setStep(6)} />
      </div>
    );
  }

  // ── STEP 6: PROPHECY ────────────────────────────────────────
  if (step === 6) {
    return (
      <div>
        <StepIndicator steps={STEPS} current={6} />
        <div style={{ fontSize: 13, color: SD.starDim, lineHeight: 1.8, marginBottom: 8, fontStyle: "italic" }}>
          A Prophecy is your alternate win condition. Meet its requirements and spend the Khao — you win.
        </div>
        <div style={{ maxHeight: "56vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          <div onClick={() => setSelectedProphecy(null)} style={{ background: selectedProphecy === null ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${selectedProphecy === null ? SD.starDim : SD.void4}`, borderRadius: 10, padding: "12px 16px", cursor: "pointer" }}>
            <div style={{ fontSize: 14, fontStyle: "italic", color: selectedProphecy === null ? SD.starSilver : SD.void4 }}>◇ No Prophecy</div>
          </div>
          {PROPHECIES.map(p => (
            <div key={p.id} onClick={() => setSelectedProphecy(p.id)} style={{ background: selectedProphecy === p.id ? `linear-gradient(135deg, ${SD.purpleDim}, ${SD.void3})` : SD.void1, border: `1px solid ${selectedProphecy === p.id ? SD.gold : SD.void4}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", boxShadow: selectedProphecy === p.id ? shimmerBorder(SD.gold) : "none", transition: "all 0.2s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic", color: selectedProphecy === p.id ? SD.gold : SD.starSilver, fontFamily: SD.fontDisplay }}>{p.name}</div>
                <div style={{ fontSize: 11, color: SD.gold }}>✦ {p.khaoCost} Khao</div>
              </div>
              <div style={{ fontSize: 11, color: SD.starDim, lineHeight: 1.6 }}>{p.effect}</div>
            </div>
          ))}
        </div>
        <NavButtons onNext={() => setStep(7)} />
      </div>
    );
  }

  // ── STEP 7: REVIEW ──────────────────────────────────────────
  if (step === 7) {
    const uhltiId = `u_${line.id}_lord`;
    const uhltiCard = UHLTIS.find(u => u.id === uhltiId);
    const affCard = AFFINITY_INFO.find(a => a.id === selectedAffinity);
    const prophCard = PROPHECIES.find(p => p.id === selectedProphecy);
    const gValid = gTotal >= 35;
    const lValid = lTotal >= 10;

    return (
      <div>
        <StepIndicator steps={STEPS} current={7} />
        <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic", color: gValid && lValid ? SD.gold : SD.red, textAlign: "center", marginBottom: 16, fontFamily: SD.fontDisplay }}>
          {gValid && lValid ? "✦ Deck Ready to Save!" : "⚠ Deck needs more cards"}
        </div>

        {[
          { label: "LINE", value: `${line.icon} ${line.name} (${line.ilk})` },
          { label: "GRIMOIRE", value: `${gTotal} cards ${gValid ? "✓" : `— need ${35-gTotal} more`}` },
          { label: "LUXMOIRE", value: `${lTotal} cards ${lValid ? "✓" : `— need ${10-lTotal} more`}` },
          { label: "U'HLTI", value: includeUhlti && uhltiCard ? `${uhltiCard.name} ✓` : "None" },
          { label: "AFFINITY", value: affCard ? `${affCard.icon} ${affCard.name}` : "None" },
          { label: "PROPHECY", value: prophCard ? prophCard.name : "None" },
        ].map(row => (
          <div key={row.label} style={{ display: "flex", gap: 12, marginBottom: 10, background: SD.void1, borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: SD.starDim, width: 80, flexShrink: 0 }}>{row.label}</div>
            <div style={{ fontSize: 13, color: SD.starSilver, fontFamily: SD.fontDisplay }}>{row.value}</div>
          </div>
        ))}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={() => setStep(6)} style={{ flex: 1, padding: "13px", background: "transparent", border: `1px solid ${SD.void4}`, borderRadius: 10, color: SD.starDim, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 13 }}>← Back</button>
          <button onClick={buildAndFinish} style={{ flex: 2, padding: "13px", background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`, border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright, cursor: "pointer", fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic", boxShadow: `0 0 16px ${GM.inkGold}22` }}>
            ✦ Build & Review in Classic →
          </button>
        </div>
      </div>
    );
  }

  return null;
}


// ─────────────────────────────────────────────────────────────
// MAIN DECK BUILDER
// ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════
// THE SET's two small parts
// ══════════════════════════════════════════════════════════════
// Both are deliberately plain. The Forge already carries a whole
// card-tile language for the things you own MANY of; a Set is six
// slots you own exactly one of each, and dressing those as cards
// would say "these are cards" about a Companion, which it is not.
// So: an engraved rule, and a slab that opens a list.

function SectionRule({ label }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 8, letterSpacing: 5, color: GM.vellumFaint,
        fontFamily: GM.font, paddingBottom: 8,
      }}>{label}</div>
      <div style={{ height: 2, background: GOLD_METAL_H, opacity: 0.55, boxShadow: `0 0 6px ${GM.inkGoldBright}33` }} />
    </div>
  );
}

// A single choose-one rift.
//
// `options` is [{id, name, sub?, tint?}] and MAY BE EMPTY — that is
// the honest state for a player who owns no Curios yet, and it says
// so rather than opening an empty menu. `value` may be null and
// picking the already-picked option clears it, so every slot can be
// emptied without a separate clear button.
function SlotPicker({ label, options, value, onPick, emptyLabel = "◇ empty", wide = false }) {
  const [open, setOpen] = useState(false);
  const chosen = options.find(o => o.id === value) || null;
  const tint = chosen?.tint || GM.inkGold;
  return (
    <div style={{ position: "relative", flex: wide ? "1 1 100%" : "1 1 170px", maxWidth: wide ? 560 : 260 }}>
      <button
        onClick={() => options.length && setOpen(v => !v)}
        style={{
          width: "100%", textAlign: "left", cursor: options.length ? "pointer" : "default",
          background: chosen ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
          border: "none",
          borderTop: `1px solid ${chosen ? `${tint}99` : "rgba(226,232,244,0.16)"}`,
          clipPath: facetTB(8), padding: "11px 13px",
          boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -3px 6px rgba(0,0,0,0.42)",
          filter: chosen
            ? `drop-shadow(0 0 9px ${tint}33) drop-shadow(0 2px 5px rgba(0,0,0,0.5))`
            : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
          fontFamily: GM.font,
        }}>
        <div style={{ fontSize: 8, letterSpacing: 4, color: GM.vellumFaint }}>{label}</div>
        <div style={{
          fontSize: 13, fontStyle: "italic", marginTop: 3,
          color: chosen ? tint : GM.vellumFaint,
          textShadow: chosen ? textGlow(tint) : TEXT_SET,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {chosen ? chosen.name : (options.length ? emptyLabel : "◇ none owned yet")}
        </div>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 60,
          marginTop: 4, maxHeight: 260, overflowY: "auto",
          background: OBSIDIAN_PANEL, clipPath: facetTB(8),
          boxShadow: "0 10px 28px rgba(0,0,0,0.7), inset 0 1px 0 rgba(226,232,244,0.12)",
        }}>
          {options.map(o => {
            const on = o.id === value;
            return (
              <button key={o.id}
                onClick={() => { onPick(on ? null : o.id); setOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: on ? `${(o.tint || GM.inkGold)}18` : "transparent",
                  border: "none", borderBottom: `1px solid ${GM.ruling}`,
                  padding: "9px 12px", cursor: "pointer", fontFamily: GM.font,
                }}>
                <div style={{
                  fontSize: 12, fontStyle: "italic",
                  color: on ? (o.tint || "#ffe9a8") : GM.vellum,
                  textShadow: on ? textGlow(o.tint || GM.inkGoldBright) : TEXT_SET,
                }}>{on ? "◆ " : ""}{o.name}</div>
                {o.sub && (
                  <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 0.5, marginTop: 2, lineHeight: 1.5 }}>
                    {o.sub.length > 96 ? `${o.sub.slice(0, 96)}…` : o.sub}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DeckBuilder({ onBack, profile = null, initialDeck = null, onSaveLoadout = null }) {
  const isStoryMode = !!profile;
  // C5 backdrop wave (2026-08-18): every DeckBuilder face lives inside
  // the ONE canonical room rectangle; roots are transparent so the
  // equipped backdrop letterboxes around it (the Emporium pattern).
  // Hook lives up here because the mode picker / quick build returns
  // below are early-outs and hooks must run unconditionally.
  const stage = useStageRect();
  // The profile prop is a snapshot captured at navigation time, so foils bought
  // in the Boutique after that won't appear. Subscribe to the live collection so
  // the Forge always reflects current ownership (buy anywhere → shows here).
  const [liveCollection, setLiveCollection] = useState(profile?.collection || null);
  // T21: live ◕ balance for the scrap surfaces (lightbox chip + marked bar).
  const [liveCoins, setLiveCoins] = useState(profile?.chocolateCoins ?? null);
  // Loose-debt fix (2026-08-18): the profile PROP is a navigation-time
  // snapshot, so re-entering the Forge from the Workshop showed the set rail
  // one write stale. The listener's FIRST fire is fresh server truth — re-seed
  // the rail from it once, before any interaction. Later fires must NOT touch
  // the rail: the player may be editing it. (Deck/editor state stays seeded
  // from initialDeck exactly as before — only the rail was lying.)
  const freshSeedRef = useRef(false);
  useEffect(() => {
    const uid = profile?.uid;
    if (!uid) return;
    const unsub = listenToProfile(uid, (p) => {
      if (!p) return;
      if (p.collection) setLiveCollection(p.collection);
      setLiveCoins(p.chocolateCoins || 0);
      if (!freshSeedRef.current) {
        freshSeedRef.current = true;
        const fresh = readSets(p);
        setSets(fresh.sets);
        setActiveSlot(fresh.active);
        setSetName(fresh.sets[fresh.active]?.name || `Set ${fresh.active + 1}`);
      }
    });
    return () => { try { unsub && unsub(); } catch (_) {} };
  }, [profile?.uid]);
  // One-time provenance backfill: stamp the player's starter-deck cards with
  // the `starter` source if they were granted before sigils existed. Idempotent
  // + append-only (see backfillStarterProvenance), so it's safe on every load
  // and only touches cards that have NO source recorded yet. This is what makes
  // existing accounts show their starter sigils without re-rolling.
  useEffect(() => {
    const uid = profile?.uid;
    const sdId = profile?.starterDeckId;
    if (!uid || !sdId) return;
    let cancelled = false;
    (async () => {
      try {
        const { getStarterDeck } = await import("./starterDecks");
        const { backfillStarterProvenance } = await import("./progression");
        const sd = getStarterDeck(sdId);
        if (!sd) return;
        // ⚠ ALL FIVE LANES. This listed grimoire + curios only, the same
        // omission Onboarding.finalize carried, so an account's Luxmoire,
        // Esclings and Prophecy never got a starter sigil. Append-only,
        // so widening it repairs every account that already onboarded.
        const ids = [
          ...Object.keys(sd.grimoire || {}),
          ...Object.keys(sd.luxmoire || {}),
          ...((sd.curios   || []).filter(Boolean)),
          ...((sd.esclings || []).filter(Boolean)),
          ...(sd.prophecy ? [sd.prophecy] : []),
        ];
        if (!cancelled) await backfillStarterProvenance(uid, ids);
      } catch (_) { /* non-fatal — sigils just won't backfill this load */ }
    })();
    return () => { cancelled = true; };
  }, [profile?.uid, profile?.starterDeckId]);
  const collection = liveCollection || profile?.collection || {};
  const ownsCard = (id) => !isStoryMode || (collection[id]?.count > 0) || (collection[id]?.holoCount > 0);

  // ── T21 · THE SCRAP LANE — Forge surface state ───────────────
  // Manual always. A MARK is per-card and means "all its plain dupes";
  // foils are precious, so they melt one at a time from the lightbox and
  // never in bulk (V'ael's call, labeled — a fat finger must not be able
  // to vaporize an Aeternus). Payouts recompute from the LIVE collection
  // at confirm time, so a stale mark can never over-pay — and the engine
  // clamps again at the write.
  const [scrapMarks, setScrapMarks] = useState({});
  const [scrapBusy, setScrapBusy] = useState(false);
  const plainDupesOf = (id) => Math.max(0, (collection[id]?.count || 0) - 1);
  const markedIds = Object.keys(scrapMarks).filter(id => scrapMarks[id] && plainDupesOf(id) > 0);
  const markedCopies = markedIds.reduce((s, id) => s + plainDupesOf(id), 0);
  const markedPay = markedCopies * scrapValueOf(null);
  const toggleScrapMark = (id) => setScrapMarks(m => ({ ...m, [id]: !m[id] }));
  const doScrap = async (cardId, tier, sourceEl) => {
    if (scrapBusy || !profile?.uid) return;
    setScrapBusy(true);
    try {
      const res = await scrapCopies(profile.uid, [{ cardId, tier, copies: 1 }]);
      if (res.success) {
        crumbleToCoins(sourceEl, { motes: tier ? 18 : 12, tierColor: tier ? (HOLO_COLORS[tier] || null) : null });
        if (res.newBalance != null) setLiveCoins(res.newBalance);
      }
    } catch (_) { /* non-fatal — the live listener re-truths the counts */ }
    finally { setScrapBusy(false); }
  };
  const scrapAllMarked = async () => {
    if (scrapBusy || !profile?.uid || markedIds.length === 0) return;
    setScrapBusy(true);
    try {
      const items = markedIds.map(id => ({ cardId: id, tier: null, copies: plainDupesOf(id) }));
      const res = await scrapCopies(profile.uid, items);
      if (res.success) {
        // The crumble is the receipt — fire it from the marked tiles
        // themselves (capped + staggered so a 30-card sweep stays classy).
        markedIds.slice(0, 8).forEach((id, i) => {
          const el = document.querySelector(`[data-scrap-tile="${id}"]`);
          if (el) setTimeout(() => crumbleToCoins(el, { motes: 10 }), i * 90);
        });
        if (res.newBalance != null) setLiveCoins(res.newBalance);
        setScrapMarks({});
      }
    } catch (_) { /* non-fatal */ }
    finally { setScrapBusy(false); }
  };
  // The scrap surface exists only where a collection does — the Forge
  // (story mode). Free Play has no profile, no dupes, no scrap.
  const scrapCtx = profile?.uid ? { coins: liveCoins, busy: scrapBusy, doScrap } : null;

  const [builderMode, setBuilderMode] = useState(onSaveLoadout ? "classic" : null);
  const [activeTab, setActiveTab] = useState("grimoire");
  // Tabs share one scroll container. Switching tabs used to leave the
  // viewport wherever it happened to be, so a player who'd scrolled deep
  // into GRIMOIRE could tap "THE SET" and land mid-scroll in Curios/
  // Companion, never seeing the top of the tab (or the starter-deck
  // banner) at all. Every tab click now resets this scroll to the top.
  const forgeScrollRef = useRef(null);
  const [grimoire, setGrimoire] = useState(initialDeck?.grimoire || {});
  const [luxmoire, setLuxmoire] = useState(initialDeck?.luxmoire || {});
  // ── CLEAN BREAK (provenance update) ──────────────────────────────
  // Old model: holoSelections = { [cardId]: tier } — ONE foil per card slot.
  // New model: deckVariants  = { [cardId]: [ {tier, source}, ... ] } — a LIST
  // of specific instances, so you can run multiple of the same card in
  // different foils AND from different sources (Solrend-from-ShadowFox +
  // Solrend-from-Vault, both legal, both playable). The list is capped at the
  // card's `count`; the FIRST entry is treated as the card's "primary" for the
  // tile preview + glow. A plain (no-foil) copy is represented by {tier:null}.
  // Old saved decks (holoSelections) intentionally do NOT carry over — the
  // collection itself is untouched; decks just default to plain until re-foiled.
  const [deckVariants, setDeckVariants] = useState(initialDeck?.deckVariants || {});
  const [filterType, setFilterType] = useState("All");
  const [filterSchool, setFilterSchool] = useState("All");
  const [filterTomeIlk, setFilterTomeIlk] = useState("All");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterHolo, setFilterHolo] = useState("All");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [statusOpen, setStatusOpen] = useState(true);
  const [sortBy, setSortBy] = useState("default");
  const [showUnowned, setShowUnowned] = useState(!isStoryMode);
  const [searchText, setSearchText] = useState("");
  const [savedGrimoires, setSavedGrimoires] = useState({});
  const [showSave, setShowSave] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [currentName, setCurrentName] = useState(null);
  const [toast, setToast] = useState(null);

  // ── THE SET (2026-08-17) ─────────────────────────────────────
  // caP'n: "a set can contain all of the following for a true
  // proper preset experience: Companion, 3 Esclings, 2 Curios,
  // Prophecy, Grimoire, Luxmoire."
  // (⚠ his words, and the count in them is pre-R110 — the battle line
  // became FOUR on 2026-09-06 and every number below is ESCLING_SLOTS,
  // never a literal. Quoting him verbatim, and saying so, rather than
  // editing his sentence.)
  //
  // Four of those six were previously invisible in the Forge.
  // Curios and Prophecy were passed straight THROUGH it untouched
  // (`curios: initialDeck?.curios`) — a player could own a Curio
  // and have no screen anywhere that would let them place it.
  // Esclings did not exist as a choice at all. So this is not a
  // preset UI bolted onto a finished Forge; it is the half of the
  // Forge that was never built, with the preset rail on top.
  //
  // All four live as ordinary state seeded from the equipped set,
  // and every one of them is allowed to be null. A set is a
  // convenience, never a gate: a player who ignores this whole tab
  // gets exactly the game they had yesterday.
  const seededSets = useMemo(() => readSets(profile), [profile]);
  const [sets, setSets] = useState(seededSets.sets);
  const [activeSlot, setActiveSlot] = useState(seededSets.active);
  const [setName, setSetName] = useState(seededSets.sets[seededSets.active]?.name || "Set 1");
  const [esclings, setEsclings] = useState(
    // ESCLING_SLOTS-wide, never a literal: the battle line grew from three
    // rifts to four, and a three-wide seed would leave the fourth picker with
    // `esclings[3] === undefined` — the Forge would render it, but nothing the
    // player chose there could ever survive a save.
    initialDeck?.esclings || seededSets.sets[seededSets.active]?.esclings || Array(ESCLING_SLOTS).fill(null)
  );
  const [curios, setCurios] = useState(
    initialDeck?.curios || seededSets.sets[seededSets.active]?.curios || [null, null]
  );
  const [prophecy, setProphecy] = useState(
    initialDeck?.prophecy || seededSets.sets[seededSets.active]?.prophecy || null
  );
  const [companion, setCompanion] = useState(
    profile?.equipped?.companion || seededSets.sets[seededSets.active]?.companion || null
  );

  const unlockedCompanions = useMemo(() => getUnlockedCompanions(profile), [profile]);

  // Starter decks fully in the player's Collection but not yet loaded
  // into any Set. Detection reads only the deck's Escendant trio (its
  // rarest, most deck-identifying cards) rather than every card, so it
  // stays cheap and never false-negatives on a partially-foiled deck.
  // Free Play has no profile and no real ownership, so it never shows.
  const unclaimedStarterDecks = useMemo(() => {
    if (!isStoryMode) return [];
    return STARTER_DECKS.filter(deck => {
      const trio = Object.keys(deck.grimoire || {}).filter(id => CARD_BY_ID[id]?.type === CARD_TYPES.ESCENDANT);
      if (trio.length === 0) return false;
      const owned = trio.every(id => (collection[id]?.count > 0) || (collection[id]?.holoCount > 0));
      if (!owned) return false;
      const alreadyLoaded = sets.some(st => trio.every(id => (st.grimoire || {})[id] > 0));
      return !alreadyLoaded;
    });
  }, [isStoryMode, collection, sets]);
  // Never offer a one-tap overwrite of a Set the player is already
  // mid-editing — only appears while the current Grimoire is blank.
  const showStarterBanner = unclaimedStarterDecks.length > 0 && Object.keys(grimoire).length === 0;

  // Switching slots inside the Forge loads that set into the
  // editor WITHOUT writing anything. Equipping is a separate,
  // explicit tap — because a player poking at slot 3 to see what
  // is in it must never lose the deck they walked in with.
  const loadSetIntoEditor = (i) => {
    const st = sets[i] || {};
    setActiveSlot(i);
    setSetName(st.name || `Set ${i + 1}`);
    setGrimoire(st.grimoire || {});
    setLuxmoire(st.luxmoire || {});
    setDeckVariants(st.variants || {});
    // Same reason as the seed above — a set slot with no stored Esclings has
    // to come back ESCLING_SLOTS-wide or tapping an empty set silently
    // shrinks the board back to the pre-2026-09-07 three-rift shape.
    setEsclings(st.esclings || Array(ESCLING_SLOTS).fill(null));
    setCurios(st.curios || [null, null]);
    setProphecy(st.prophecy || null);
    setCompanion(st.companion || null);
    setCurrentName(null);
  };

  // Loads a STARTER_DECKS entry straight into the editor — the one-tap
  // answer to "I bought a starter deck, now how do I play it." Reuses
  // STARTER_DECKS as the only recipe (no second hand-maintained table to
  // drift out of sync), and is identical for all thirteen decks — canon
  // or Experimental — so it never needs a special case.
  const loadStarterDeckIntoEditor = (deck) => {
    setGrimoire({ ...deck.grimoire });
    setLuxmoire({ ...deck.luxmoire });
    setDeckVariants({});
    // A starter deck that names no Esclings still has to fill the whole line;
    // a three-wide literal here would hand the editor an array one shorter
    // than ESCLING_SLOTS and the fourth rift would have nowhere to write to.
    setEsclings(deck.esclings ? [...deck.esclings] : Array(ESCLING_SLOTS).fill(null));
    setCurios(deck.curios ? [...deck.curios] : [null, null]);
    setProphecy(deck.prophecy || null);
    setSetName(deck.name);
    setCurrentName(null);
    setActiveTab("theset");
    forgeScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    showToastMsg(`✦ ${deck.name} loaded — press ◆ ${onSaveLoadout ? "Equip" : "Save"} when ready.`, SD.gold);
  };

  // Escape priority: Save > Load > back/picker
  // In Forge (onSaveLoadout present): Escape always exits, never shows picker
  useModalKeys(
    showSave ? () => setShowSave(false) :
    showLoad ? () => setShowLoad(false) :
    !builderMode ? onBack :
    onSaveLoadout ? onBack :          // Forge — escape exits, no picker
    () => setBuilderMode(null)        // Free play — escape returns to picker
  );

  // ── Variant helpers (the deckVariants instance list) ─────────────
  // The PRIMARY tier of a card = the first instance's tier (drives the tile
  // preview + glow). Falls back to the collection's legacy holoTier so a card
  // shows its known foil even before the player touches the chooser.
  const primaryTierOf = (cardId) => {
    const list = deckVariants[cardId];
    if (Array.isArray(list) && list.length) return list[0].tier || null;
    return null;
  };

  // Set a card's foil to a single tier with NO pinned source (the simple path:
  // Free Play, or a tier owned from only one/zero sources). Replaces the whole
  // instance list with one entry, then pads/truncates to the card's count.
  const selectHolo = (cardId, tier) => {
    setDeckVariants(prev => {
      const copy = { ...prev };
      if (!tier) { delete copy[cardId]; return copy; }
      copy[cardId] = [{ tier, source: null }];
      return copy;
    });
  };

  // Add a SPECIFIC {tier, source} instance to a card (the multi-variant path).
  // Appends if there's room under the card's count; if the slot is full, it
  // rolls the oldest entry off the front so the newest pick always lands. This
  // is how two copies of one card end up with two different sigils.
  const selectVariant = (cardId, tier, source) => {
    const max = (CARD_BY_ID[cardId]?.deckLimit) || 1;
    const inDeck = (grimoire[cardId] || luxmoire[cardId] || 0);
    const cap = Math.max(1, Math.min(max, inDeck || max));
    setDeckVariants(prev => {
      const list = Array.isArray(prev[cardId]) ? [...prev[cardId]] : [];
      list.push({ tier: tier || null, source: source || null });
      while (list.length > cap) list.shift();
      return { ...prev, [cardId]: list };
    });
  };

  // Which foils a card may show as swatches.
  //   Free Play  → every tier the game defines (reads the master TIER_ORDER
  //                live, so a new holo added to PackArt.js appears here with
  //                ZERO changes to this file).
  //   Forge/Story → only the foils this card is actually OWNED in. UNIONS
  //                the per-tier holoTiers map (new schema) WITH the legacy
  //                single holoTier, so foils bought before the holoTiers
  //                schema shipped are never orphaned. Returns [] when no
  //                foil is owned → the marquee renders nothing (plain card).
  const availableTiersFor = (cardId) => {
    if (!isStoryMode) return TIER_ORDER;
    const rec = collection[cardId];
    if (!rec) return [];
    const owned = new Set();
    if (rec.holoTiers && typeof rec.holoTiers === "object") {
      for (const t of Object.keys(rec.holoTiers)) {
        if ((rec.holoTiers[t] || 0) > 0) owned.add(t);
      }
    }
    // Legacy single-tier record (pre-schema purchases) joins the union.
    if (rec.holoTier) owned.add(rec.holoTier);
    // Emit in canonical master order; ignore any unknown keys.
    return TIER_ORDER.filter(t => owned.has(t));
  };

  // Build synthetic collection for Shatterfield from the chosen variants. For
  // now Shatterfield consumes one foil per card (primary = first instance);
  // per-instance in-duel rendering + the source sigil on the battlefield are
  // the deferred Shatterfield solo pass. We also pass `deckVariants` through on
  // the loadout so that future surface keeps the full provenance list.
  const buildHoloCollection = () => {
    const col = { ...collection };
    Object.entries(deckVariants).forEach(([id, list]) => {
      const tier = Array.isArray(list) && list.length ? list[0].tier : null;
      if (tier) col[id] = { ...(col[id] || { count: 1 }), holoTier: tier, holoCount: 1 };
    });
    return col;
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Remove old test decks
        const TEST_DECKS = ["Yes :3", "bless"];
        let changed = false;
        TEST_DECKS.forEach(name => { if (parsed[name]) { delete parsed[name]; changed = true; } });
        if (changed) localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        setSavedGrimoires(parsed);
      }
    } catch (e) {}
  }, []);

  const showToastMsg = (msg, color = SD.purpleLight) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = (name) => {
    const lanes = enforceLanesOnSave(grimoire, luxmoire);
    if (!lanes) return; // illegal save blocked — toast already shown
    if (lanes.grim !== grimoire) { setGrimoire(lanes.grim); setLuxmoire(lanes.lux); }
    const updated = { ...savedGrimoires, [name]: { grimoire: lanes.grim, luxmoire: lanes.lux, deckVariants, savedAt: new Date().toISOString() } };
    setSavedGrimoires(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setCurrentName(name); setShowSave(false);
    showToastMsg(`✦ "${name}" saved!`);
    
    // Fire first forge achievement if this is their first saved deck
    const hadPriorDecks = Object.keys(savedGrimoires).length > 0;
    if (!hadPriorDecks && profile?.uid) {
      // Import here to avoid circular dependency
      import("./achievementWrites").then(({ grantAchievement }) => {
        grantAchievement(profile.uid, "terror:first_forge", "deckBuilder:save");
      });
    }
  };

  const handleLoad = (name) => {
    const deck = savedGrimoires[name];
    if (deck) { setGrimoire(deck.grimoire || {}); setLuxmoire(deck.luxmoire || {}); setDeckVariants(deck.deckVariants || {}); setCurrentName(name); setShowLoad(false); showToastMsg(`✦ "${name}" loaded!`); }
  };

  const handleDelete = (name) => {
    const updated = { ...savedGrimoires };
    delete updated[name];
    setSavedGrimoires(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (currentName === name) setCurrentName(null);
    showToastMsg(`"${name}" deleted.`, SD.red);
  };

  const handleQuickBuildFinish = (newGrimoire, newLuxmoire, name, prophecy) => {
    setGrimoire(newGrimoire);
    setLuxmoire(newLuxmoire);
    setCurrentName(name);
    // Store prophecy selection in localStorage alongside deck
    if (prophecy) {
      const stored = localStorage.getItem(STORAGE_KEY);
      const grimoires = stored ? JSON.parse(stored) : {};
      grimoires[name] = { grimoire: newGrimoire, luxmoire: newLuxmoire, prophecy, savedAt: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(grimoires));
      setSavedGrimoires(grimoires);
    }
    setBuilderMode("classic");
    showToastMsg(`✦ ${name} built! Review and save.`, SD.gold);
  };

  // ── R60 — max 1 Affinity per Luxmoire (across ALL names) ──
  const affinityTotal = (deckObj) => Object.entries(deckObj || {}).reduce((n, [cid, q]) =>
    n + (CARD_BY_ID[cid]?.type === "Affinity" ? (q || 0) : 0), 0);
  // ── R104 — max 3 U'hltis per Luxmoire, TOTAL (caP'n, 2026-08-26) ──
  //   *"U'hltis rules as it was in Grimoire just for the Lux, which
  //   should be 3 U'hltis if I'm not mistaken."*
  // R59 already caps each NAME at 1 through the per-card `max` the
  // picker passes in; this is the LANE total, which no per-name cap can
  // express — three names at one copy each is legal, a fourth is not.
  const uhltiTotal = (deckObj) => Object.entries(deckObj || {}).reduce((n, [cid, q]) =>
    n + (CARD_BY_ID[cid]?.type === "U'hlti" ? (q || 0) : 0), 0);

  const addCard = (id, deck, setDeck, max) => {
    const c = deck[id] || 0;
    if (CARD_BY_ID[id]?.type === "Affinity" && affinityTotal(deck) >= 1) {
      showToastMsg("Only 1 Affinity per Luxmoire (R60).", SD.red); return;
    }
    if (CARD_BY_ID[id]?.type === "U'hlti" && uhltiTotal(deck) >= LUXMOIRE_UHLTI_MAX) {
      showToastMsg(`Only ${LUXMOIRE_UHLTI_MAX} U'hltis per Luxmoire (R104).`, SD.red); return;
    }
    if (c < max) setDeck({ ...deck, [id]: c + 1 });
  };

  // ── LANE ENFORCEMENT ON SAVE ────────────────────────────────
  // Legacy decks are grandfathered on LOAD; on SAVE the lanes are made
  // true. U'hltis + Affinities found in the Grimoire migrate to the
  // Luxmoire automatically; pre-placed types (Curio / Prophecy /
  // Escling) are stripped from both lanes. Returns null (blocked, with
  // toast) only when the result is still illegal — >1 Affinity or a
  // Luxmoire over 40.
  const enforceLanesOnSave = (g, l) => {
    const grim = { ...(g || {}) };
    const lux  = { ...(l || {}) };
    let migrated = 0, stripped = 0;
    Object.keys(grim).forEach(cid => {
      const t = CARD_BY_ID[cid]?.type;
      if (t === "U'hlti" || t === "Affinity") {
        lux[cid] = (lux[cid] || 0) + grim[cid]; migrated += grim[cid]; delete grim[cid];
      } else if (t === "Curio" || t === "Prophecy" || t === "Escling") {
        stripped += grim[cid]; delete grim[cid];
      }
    });
    Object.keys(lux).forEach(cid => {
      const t = CARD_BY_ID[cid]?.type;
      if (t === "Curio" || t === "Prophecy" || t === "Escling") { stripped += lux[cid]; delete lux[cid]; }
    });
    const affs = affinityTotal(lux);
    if (affs > 1) { showToastMsg(`Illegal deck — ${affs} Affinities in the Luxmoire (max 1, R60).`, SD.red); return null; }
    // ── R104 · THE U'HLTI TOTAL, CHECKED AT THE SAME DOOR AS R60 ──
    // The migration above can PUSH U'hltis into the Luxmoire out of a
    // legacy Grimoire, so this has to be asked AFTER it runs and not
    // before: a deck that was legal under the old lanes can arrive here
    // holding four, and telling a player "saved" and then handing them
    // an illegal deck is the failure mode the minimums block exists to
    // describe. Blocked, named and numbered, like every other refusal
    // in this function.
    const uhl = uhltiTotal(lux);
    if (uhl > LUXMOIRE_UHLTI_MAX) {
      showToastMsg(`Illegal deck — ${uhl} U'hltis in the Luxmoire (max ${LUXMOIRE_UHLTI_MAX}, R104).`, SD.red);
      return null;
    }
    const lCount = Object.values(lux).reduce((a, b) => a + b, 0);
    const gCount = Object.values(grim).reduce((a, b) => a + b, 0);
    if (lCount > LUXMOIRE_MAX) { showToastMsg(`Illegal deck — Luxmoire ${lCount}/${LUXMOIRE_MAX} (R06).`, SD.red); return null; }
    // ═══════════════════════════════════════════════════════════
    // ⭐ THE MINIMUMS WERE DECORATIVE. THE MAXIMUM WAS NOT.
    // ═══════════════════════════════════════════════════════════
    // This function checked the Luxmoire CEILING and the Affinity cap
    // and nothing else, so R05 (Grimoire ≥ 35) and R06's floor
    // (Luxmoire ≥ 10) were numbers the Rulebook printed and the game
    // did not believe. A player could equip a THREE-CARD Grimoire and
    // an EMPTY Luxmoire and walk into a real duel: they Graveshift on
    // turn two for 10 Anima, their Luxmoire draw tile does nothing, and
    // the smallest legal-looking deck is by a distance the most
    // consistent one in the game. In PvP that is not an exploit to be
    // discovered, it is the obvious play.
    //
    // Four validators exist for exactly this — validateGrimoireSize,
    // validateLuxmoireSize, validateDeckLanes, validateDeckLimits — and
    // not one of them has ever executed, because their only route is
    // enforce("DECK_...") and nothing dispatches those types.
    //
    // Checked HERE, at save, rather than at duel start, deliberately:
    // the moment to tell somebody their deck is short is while they are
    // looking at the deck, not while an opponent is waiting. Both
    // messages name the rule and print the number, because "illegal
    // deck" with no arithmetic is a wall.
    //
    // Safe for every existing account: all twelve starter decks are 36+
    // Grimoire and 11+ Luxmoire. Nothing anyone owns becomes unsavable.
    if (gCount < GRIMOIRE_MIN) {
      showToastMsg(`Illegal deck — Grimoire ${gCount}/${GRIMOIRE_MIN} minimum (R05).`, SD.red);
      return null;
    }
    if (lCount < LUXMOIRE_MIN) {
      showToastMsg(`Illegal deck — Luxmoire ${lCount}/${LUXMOIRE_MIN} minimum (R06).`, SD.red);
      return null;
    }
    if (migrated > 0) showToastMsg(`◆ ${migrated} U'hlti/Affinity card${migrated > 1 ? "s" : ""} moved to the Luxmoire (the lanes — R05).`, SD.gold);
    if (stripped > 0) showToastMsg(`◆ ${stripped} pre-placed card${stripped > 1 ? "s" : ""} removed — Curios & Prophecies never live in a deck.`, SD.gold);
    return { grim, lux };
  };
  const removeCard = (id, deck, setDeck) => {
    const c = deck[id] || 0;
    const next = c <= 1 ? 0 : c - 1;
    if (next === 0) { const u = { ...deck }; delete u[id]; setDeck(u); }
    else setDeck({ ...deck, [id]: next });
    // Keep the instance list coherent: never more variants than copies in deck.
    // Removing the last copy clears the card's variants entirely.
    setDeckVariants(prev => {
      if (!prev[id]) return prev;
      if (next === 0) { const u = { ...prev }; delete u[id]; return u; }
      const list = prev[id].slice(0, next);
      return { ...prev, [id]: list };
    });
  };

  const displayCards = useMemo(() => {
    const pool = activeTab === "luxmoire" ? ALL_LUXMOIRE_CARDS : ALL_GRIMOIRE_CARDS;
    let cards = pool.filter(card => {
      const matchType  = filterType === "All" || card.type === filterType;
      // TIGHTENED 2026-08-25. This chip row offers Space/Phantom/Time/
      // Dragon — the SCHOOL axis — and used to match either field
      // because the data stored those words under `ilk`. It doesn't
      // any more, so this asks the one field that holds them.
      const matchSchool = filterSchool === "All" || card.school === filterSchool;
      const matchTomeIlk = filterTomeIlk === "All" || card.type !== "Tome" || card.ilk === filterTomeIlk;
      const matchLevel = filterLevel === "All" || String(card.level) === filterLevel || (filterLevel === "xxx" && card.level === "xxx");
      const matchSearch = !searchText || card.name.toLowerCase().includes(searchText.toLowerCase()) || (card.effect?.toLowerCase().includes(searchText.toLowerCase()));
      const owned = ownsCard(card.id);
      const matchOwned = showUnowned || owned;
      const holoData = collection[card.id];
      const hasHolo = holoData?.holoCount > 0;
      const holoTier = holoData?.holoTier;
      const matchHolo = filterHolo === "All" || (filterHolo === "any_holo" && hasHolo) ||
        (filterHolo === holoTier && hasHolo);
      return matchType && matchSchool && matchTomeIlk && matchLevel && matchSearch && matchOwned && matchHolo;
    });
    // Sort
    if (sortBy === "cost_asc")   cards = [...cards].sort((a,b) => (a.khaoCost??0) - (b.khaoCost??0));
    if (sortBy === "cost_desc")  cards = [...cards].sort((a,b) => (b.khaoCost??0) - (a.khaoCost??0));
    if (sortBy === "level_asc")  cards = [...cards].sort((a,b) => (Number(a.level)||0) - (Number(b.level)||0));
    if (sortBy === "level_desc") cards = [...cards].sort((a,b) => (Number(b.level)||0) - (Number(a.level)||0));
    if (sortBy === "lyt_desc")   cards = [...cards].sort((a,b) => (b.lyt||0) - (a.lyt||0));
    if (sortBy === "name_asc")   cards = [...cards].sort((a,b) => a.name.localeCompare(b.name));
    return cards;
  }, [activeTab, filterType, filterSchool, filterTomeIlk, filterLevel, filterHolo, sortBy, showUnowned, searchText, collection]);

  // Reset tome ilk filter when switching away from Tome type
  const handleTypeFilter = (t) => {
    setFilterType(t);
    if (t !== "Tome") setFilterTomeIlk("All");
  };

  // Filter chip component (inline)
  const Chips = ({ label, options, value, onChange, colors = {} }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* Label — manuscript annotation */}
      <div style={{
        fontSize: 7, color: GM.vellumFaint,
        letterSpacing: 4, fontWeight: 900,
        textTransform: "uppercase", fontFamily: GM.font,
      }}>{label}</div>
      {/* Chips — wax stamp style */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {options.map(opt => {
          const isActive = value === opt.v;
          const c = colors[opt.v] || GM.inkGold;
          return (
            <button
              key={opt.v}
              onClick={() => onChange(opt.v)}
              style={{
                padding: "5px 12px",
                borderRadius: 8,
                border: `1px solid ${isActive ? c : GM.pageBorder}`,
                background: isActive
                  ? `linear-gradient(135deg, ${c}26, transparent 60%), ${OBSIDIAN_SLAB_LIT}`
                  : OBSIDIAN_SLAB,
                color: isActive ? c : GM.vellumDim,
                cursor: "pointer",
                fontSize: 10,
                fontWeight: isActive ? 900 : 400,
                fontFamily: GM.font,
                letterSpacing: isActive ? 1 : 0.5,
                transition: "all 0.15s",
                whiteSpace: "nowrap",
                boxShadow: isActive
                  ? `inset 0 1px 0 ${c}44, 0 0 10px ${c}33`
                  : "inset 0 1px 0 rgba(226,232,244,0.1), 0 2px 6px rgba(0,0,0,0.45)",
                textShadow: TEXT_SET,
              }}>
              {opt.l}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── MODE SELECT ────────────────────────────────────────────
  // ── SAVE THE SET ──────────────────────────────────────────
  // ONE payload builder, used by both save buttons. Before today
  // the two paths built the object separately and the inline one
  // in the top bar had already drifted — it re-read Curios and
  // Prophecy off `initialDeck` instead of off state, which meant
  // any edit to them was silently discarded on save. Two builders
  // for one shape is how that happens; there is now one.
  const buildSetPayload = (lanes) => ({
    name: (setName || `Set ${activeSlot + 1}`).trim().slice(0, 24),
    grimoire: lanes.grim,
    luxmoire: lanes.lux,
    variants: deckVariants,
    // `deckVariants` is kept in the payload under its old name as
    // well, because saveLoadout (the no-slot path) still writes the
    // flat loadout shape that App and the launchers read.
    deckVariants,
    collection: buildHoloCollection(),
    curios,
    prophecy,
    esclings,
    companion,
  });

  const handleSaveLoadout = async () => {
    const lanes = enforceLanesOnSave(grimoire, luxmoire);
    if (!lanes) return; // illegal save blocked — toast already shown
    if (lanes.grim !== grimoire) { setGrimoire(lanes.grim); setLuxmoire(lanes.lux); }
    if (!onSaveLoadout) return;
    await onSaveLoadout(buildSetPayload(lanes), activeSlot);
    // Keep the local rail honest without a round trip — the player
    // must see the slot they just wrote carrying their name.
    setSets(prev => prev.map((st, i) => (i === activeSlot
      ? { ...st, name: (setName || `Set ${i + 1}`).trim().slice(0, 24), grimoire: lanes.grim, luxmoire: lanes.lux, variants: deckVariants, curios, prophecy, esclings, companion }
      : st)));
    showToastMsg("✦ Set saved.", SD.gold);
  };

  // Forge never shows the mode picker — always classic
  if (onSaveLoadout && !builderMode) { onBack(); return null; }

  if (!builderMode) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontFamily: SD.fontDisplay }}>
      <div style={{ position: "relative", width: stage.w, height: stage.h, flexShrink: 0, background: OBSIDIAN_BG, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, boxSizing: "border-box", overflow: "hidden" }}>
        <SwirlField />
        {toast && <div style={{ position: "fixed", top: 14, left: "50%", transform: "translateX(-50%)", background: OBSIDIAN_SLAB, border: `1px solid ${toast.color}`, color: toast.color, padding: "10px 22px", borderRadius: 8, fontSize: 12, zIndex: 200, boxShadow: SHADOW_RAISED }}>{toast.msg}</div>}
        <div style={{ width: "100%", maxWidth: 440, maxHeight: "100%", overflowY: "auto", background: OBSIDIAN_PANEL, border: `1px solid ${GM.pageBorder}`, borderRadius: 20, padding: "36px 30px", boxShadow: SHADOW_FLOAT, position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 32, fontWeight: 900, fontStyle: "italic", color: "#f0cc60", textShadow: textGlow(GM.inkGoldBright), letterSpacing: 4, textAlign: "center", marginBottom: 6 }}>Terror</div>
          <div style={{ fontSize: 12, letterSpacing: 4, color: SD.starDim, textAlign: "center", marginBottom: 24 }}>DECK BUILDER</div>

          <Divider />

          {/* Quick Build */}
          <div onClick={() => setBuilderMode("quick")} style={{ background: `linear-gradient(135deg, ${GM.amethystDim}55, transparent 60%), ${OBSIDIAN_SLAB_LIT}`, border: `1px solid ${GM.inkGold}`, borderRadius: 14, padding: "20px 22px", cursor: "pointer", marginBottom: 14, boxShadow: `0 0 14px ${GM.inkGoldBright}44, inset 0 1px 0 rgba(245,216,120,0.3), ${SHADOW_RAISED}`, transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 28 }}>🌱</span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: SD.gold, fontFamily: SD.fontDisplay }}>Quick Build</div>
                <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 2 }}>NEW PLAYER FRIENDLY</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: SD.starSilver, lineHeight: 1.7, paddingLeft: 40 }}>
              Pick your Escendant line and we'll guide you through building a complete, legal deck step by step. Perfect for your first Grimoire.
            </div>
          </div>

          {/* Classic */}
          <div onClick={() => setBuilderMode("classic")} style={{ background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`, borderRadius: 14, padding: "20px 22px", cursor: "pointer", marginBottom: 14, boxShadow: SHADOW_RAISED, transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <StatGlyph kind="lyt" size={26} color={SD.starSilver} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: SD.starSilver, fontFamily: SD.fontDisplay }}>Classic Builder</div>
                <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 2 }}>VETERAN MODE</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: SD.starDim, lineHeight: 1.7, paddingLeft: 40 }}>
              Full card browser with filters, live deck status, and complete manual control. For Aenarchists who know what they want.
            </div>
          </div>

          {Object.keys(savedGrimoires).length > 0 && (
            <div onClick={() => { setBuilderMode("classic"); setShowLoad(true); }} style={{ background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`, borderRadius: 14, padding: "16px 22px", cursor: "pointer", marginBottom: 14, boxShadow: SHADOW_RAISED, transition: "all 0.2s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 28 }}>📖</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, fontStyle: "italic", color: SD.starSilver, fontFamily: SD.fontDisplay }}>Load Grimoire</div>
                  <div style={{ fontSize: 10, color: SD.starDim }}>{Object.keys(savedGrimoires).length} saved deck{Object.keys(savedGrimoires).length !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>
          )}

          <Divider />
          <button onClick={onBack} style={{ width: "100%", padding: "13px", background: "transparent", border: `1px solid ${SD.void4}`, borderRadius: 10, color: SD.starDim, fontSize: 13, fontStyle: "italic", cursor: "pointer", fontFamily: SD.fontDisplay }}>← Back to Menu</button>
        </div>
      </div>
      </div>
    );
  }

  // ── QUICK BUILD ────────────────────────────────────────────
  if (builderMode === "quick") {
    return (
      <div style={{ position: "fixed", inset: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontFamily: SD.fontDisplay }}>
      <div style={{ position: "relative", width: stage.w, height: stage.h, flexShrink: 0, background: OBSIDIAN_BG, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, boxSizing: "border-box", overflow: "hidden" }}>
        <SwirlField />
        <div style={{ width: "100%", maxWidth: 480, background: OBSIDIAN_PANEL, border: `1px solid ${GM.pageBorder}`, borderRadius: 14, padding: "28px 26px", maxHeight: "100%", overflowY: "auto", boxSizing: "border-box", boxShadow: SHADOW_FLOAT, position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <button aria-label="Back" onClick={() => setBuilderMode(null)} style={{ background: "transparent", border: `1px solid ${SD.void4}`, color: SD.starDim, padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 12 }}>←</button>
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, fontStyle: "italic", color: SD.gold, fontFamily: SD.fontDisplay }}>Quick Build</div>
              <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 3 }}>GUIDED DECK CREATION</div>
            </div>
          </div>
          <QuickBuild onFinish={handleQuickBuildFinish} onBack={() => setBuilderMode(null)} />
        </div>
      </div>
      </div>
    );
  }

  // ── CLASSIC BUILDER — Redesigned ──────────────────────────
  // Aesthetic: Dark luxury grimoire. Engraved typography.
  // Candlelit vault. Rune seals not buttons. Breathing space.
  const ACCENT = GM.inkGold;
  const tabDefs = [
    { id: "grimoire", label: "Grimoire",  symbol: "📖" },
    { id: "luxmoire", label: "Luxmoire",  symbol: "✦"  },
    { id: "mydeck",   label: "My Deck",   symbol: "⚔"  },
    // Forge only. The free-play builder has no profile, no owned
    // companions and nothing to equip a set ONTO, so offering the
    // tab there would be four pickers that cannot be saved.
    ...(onSaveLoadout ? [{ id: "theset", label: "The Set", symbol: "◈" }] : []),
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, background: "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", color: GM.vellum, fontFamily: GM.font,
    }}>
    <div style={{
      position: "relative", width: stage.w, height: stage.h, flexShrink: 0,
      background: OBSIDIAN_BG, overflow: "hidden",
    }}>
      {/* The Forge's living air — stage-level, holds still through the
          inner scroll (content scrolls in its own layer below) */}
      <SwirlField />
      {/* Glass-grain texture overlay (was parchment; reads as obsidian grain) */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='parchment'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='6' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0.3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23parchment)' opacity='0.06'/%3E%3C/svg%3E")`,
        opacity: 0.8,
      }} />

      {showSave && <SaveModal onSave={handleSave} onClose={() => setShowSave(false)} existing={Object.keys(savedGrimoires)} />}
      {showLoad && <LoadModal grimoires={savedGrimoires} onLoad={handleLoad} onDelete={handleDelete} onClose={() => setShowLoad(false)} />}

      {/* THE SCROLLER — content scrolls INSIDE the rectangle (Emporium
          pattern); the sticky top bar sticks against this scroller. */}
      <div ref={forgeScrollRef} style={{ position: "absolute", inset: 0, overflowY: "auto", overflowX: "hidden", paddingBottom: 64, zIndex: 1 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 16, left: "50%",
          transform: "translateX(-50%)",
          background: OBSIDIAN_SLAB,
          border: `1px solid ${toast.color}`,
          color: toast.color, padding: "10px 24px",
          borderRadius: 8, fontSize: 11,
          fontStyle: "italic", letterSpacing: 2,
          zIndex: 200, pointerEvents: "none",
          boxShadow: `0 0 20px ${toast.color}22`,
        }}>{toast.msg}</div>
      )}

      {/* ── TOP BAR ─────────────────────────────────────────── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: `linear-gradient(180deg, ${GM.pageWarm}fc 0%, ${GM.page}f8 100%)`,
        borderBottom: "none",
        backdropFilter: "blur(8px)",
        padding: "0 20px",
        boxShadow: `0 4px 24px rgba(0,0,0,0.6), inset 0 -1px 0 ${GM.inkGold}22`,
      }}>
        {/* Row 1: Nav + title + actions */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 0 10px",
          gap: 16,
        }}>
          {/* Left: back + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button
              onClick={onSaveLoadout ? onBack : () => setBuilderMode(null)}
              style={{
                background: OBSIDIAN_SLAB, border: "none",
                borderTop: "1px solid rgba(226,232,244,0.2)",
                clipPath: facetTB(7),
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -3px 6px rgba(0,0,0,0.4)",
                filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
                color: GM.vellumDim, padding: "8px 15px",
                cursor: "pointer",
                fontFamily: GM.font, fontSize: 11,
                letterSpacing: 1, transition: "all 0.2s", textShadow: TEXT_SET,
              }}>
              {onSaveLoadout ? "← Map" : "← Modes"}
            </button>
            <div>
              <div style={{
                fontSize: 20, fontWeight: 900, fontStyle: "italic",
                color: "#f0cc60", textShadow: textGlow(GM.inkGoldBright),
                letterSpacing: 4, lineHeight: 1.1,
              }}>Terror</div>
              <div style={{
                fontSize: 8, letterSpacing: 6,
                color: GM.vellumFaint, marginTop: 1,
              }}>
                {currentName
                  ? `◆ ${currentName.toUpperCase()}`
                  : onSaveLoadout ? "THE FORGE" : "CLASSIC BUILDER"}
              </div>
            </div>
          </div>

          {/* Right: actions */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              onClick={() => { setGrimoire({}); setLuxmoire({}); setCurrentName(null); showToastMsg("New Grimoire.", SD.gold); }}
              style={{
                background: OBSIDIAN_SLAB, border: "none",
                borderTop: "1px solid rgba(226,232,244,0.18)",
                clipPath: facetTB(6),
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -3px 6px rgba(0,0,0,0.4)",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                color: GM.vellumFaint, padding: "8px 14px",
                cursor: "pointer",
                fontFamily: GM.font, fontSize: 11, letterSpacing: 1, textShadow: TEXT_SET,
              }}>+ New</button>
            <button
              onClick={() => setShowLoad(true)}
              style={{
                background: OBSIDIAN_SLAB, border: "none",
                borderTop: "1px solid rgba(226,232,244,0.18)",
                clipPath: facetTB(6),
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -3px 6px rgba(0,0,0,0.4)",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                color: GM.vellumFaint, padding: "8px 14px",
                cursor: "pointer",
                fontFamily: GM.font, fontSize: 11, letterSpacing: 1, textShadow: TEXT_SET,
              }}>📖 Load</button>
            <button
              onClick={onSaveLoadout
                ? async () => { await handleSaveLoadout(); onBack(); }
                : () => setShowSave(true)}
              style={{
                background: `linear-gradient(135deg, ${GM.inkGold}, ${GM.inkGoldDim})`,
                border: "none", borderTop: "1px solid #f5d878",
                clipPath: facetTB(7),
                color: GM.page, padding: "8px 19px",
                cursor: "pointer",
                fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                fontStyle: "italic", letterSpacing: 1,
                boxShadow: "inset 0 1px 0 rgba(245,216,120,0.6)",
                filter: `drop-shadow(0 0 8px ${GM.inkGoldBright}55) drop-shadow(0 2px 5px rgba(0,0,0,0.5))`,
              }}>◆ {onSaveLoadout ? "Equip" : "Save"}</button>
          </div>
        </div>

        {/* Deck status — compact inline */}
        {/* Collapsible deck status */}
        <div style={{ marginBottom: 4 }}>
          <button onClick={() => setStatusOpen(v => !v)} style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "transparent", border: "none", cursor: "pointer",
            padding: "10px 0 8px", color: GM.vellumFaint, fontFamily: GM.font,
            fontSize: 9, letterSpacing: 3,
          }}>
            <span>◆ DECK STATUS</span>
            <span style={{ fontSize: 11, transition: "transform 0.2s", display: "inline-block", transform: statusOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
          </button>
          {statusOpen && <DeckStatusPanel grimoire={grimoire} luxmoire={luxmoire} />}
        </div>

        {/* Tab nav — engraved feel */}
        <div style={{
          display: "flex", gap: 0,
          marginTop: 8,
          position: "relative",
        }}>
          {tabDefs.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); forgeScrollRef.current?.scrollTo({ top: 0, behavior: "auto" }); }} style={{
                flex: 1, padding: "12px 4px 11px",
                border: "none",
                borderBottom: active ? `2px solid ${GM.inkGold}` : "2px solid transparent",
                background: active ? `linear-gradient(180deg, ${GM.inkGold}16, transparent)` : "transparent",
                color: active ? "#ffe9a8" : GM.vellumFaint,
                textShadow: active ? textGlow(GM.inkGoldBright) : TEXT_SET,
                cursor: "pointer",
                fontSize: 11, fontWeight: active ? 900 : 400,
                letterSpacing: active ? 3 : 2,
                fontFamily: GM.font,
                transition: "all 0.2s",
                textTransform: "uppercase",
              }}>
                {tab.symbol} {tab.label}
              </button>
            );
          })}
        </div>
        {/* Forged-gold rule — the vault's seam of light */}
        <div style={{ height: 2, background: GOLD_METAL_H, boxShadow: `0 0 8px ${GM.inkGoldBright}44` }} />
      </div>

      {/* ── CONTENT ─────────────────────────────────────────── */}
      <div style={{ padding: "20px 16px 0", position: "relative", zIndex: 1,
        borderLeft: `1px solid ${GM.ruling}`, borderRight: `1px solid ${GM.ruling}` }}>

        {/* ── STARTER DECK BANNER ────────────────────────
            Visible on every tab (top of the shared scroll), so it can
            never be scrolled past unnoticed the way a tab-local widget
            could. Disappears the moment its deck is loaded into a Set. */}
        {showStarterBanner && (
          <div style={{
            background: OBSIDIAN_SLAB_LIT, border: "none",
            borderTop: `1px solid ${GM.inkGoldBright}`,
            clipPath: facetTB(9), padding: "14px 18px", marginBottom: 18,
            boxShadow: `inset 0 1px 0 rgba(245,216,120,0.35), inset 0 -4px 8px rgba(0,0,0,0.45)`,
            filter: `drop-shadow(0 0 10px ${GM.inkGoldBright}44) drop-shadow(0 2px 6px rgba(0,0,0,0.5))`,
          }}>
            <div style={{ fontSize: 9, letterSpacing: 3, color: GM.vellumFaint, marginBottom: 10 }}>
              ✦ NEW IN YOUR COLLECTION
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
              {unclaimedStarterDecks.map(deck => (
                <div key={deck.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, fontStyle: "italic", color: "#ffe9a8", textShadow: textGlow(GM.inkGoldBright) }}>
                    {deck.name}
                  </span>
                  <button onClick={() => loadStarterDeckIntoEditor(deck)} style={{
                    background: `linear-gradient(135deg, ${GM.inkGold}, ${GM.inkGoldDim})`,
                    border: "none", borderTop: "1px solid #f5d878",
                    clipPath: facetTB(7), color: GM.page, padding: "7px 16px",
                    cursor: "pointer", fontFamily: GM.font, fontSize: 10, fontWeight: 900,
                    fontStyle: "italic", letterSpacing: 1,
                    boxShadow: "inset 0 1px 0 rgba(245,216,120,0.6)",
                  }}>◆ Load into {setName || `Set ${activeSlot + 1}`}</button>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 1, marginTop: 10, lineHeight: 1.6 }}>
              Loads the whole deck — Grimoire, Luxmoire, Esclings, Curios, and Prophecy — into the Set you’re editing.
              Press ◆ {onSaveLoadout ? "Equip" : "Save"} when you’re ready to play it.
            </div>
          </div>
        )}

        {/* ── THE SET tab ─────────────────────────────────────
            The four things a Solrend needs that are not the deck:
            the three rifts you open on, the two Curios and the
            Prophecy that sit pre-placed, and who stands beside you.
            Plus the three slots those six things get saved into.

            Everything here is OPTIONAL by construction. A null
            Escling falls to the deck-derived one, a null Curio is
            an empty rift, and a null companion leaves whoever is
            already walking with you alone. There is no state of
            this tab that can make a match unplayable, which is
            what lets it ship without a migration or a gate. */}
        {activeTab === "theset" && (
          <div style={{ paddingBottom: 40 }}>

            {/* ── THE THREE SETS ───────────────────────────── */}
            <SectionRule label="THE SETS" />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
              {sets.map((st, i) => {
                const editing = i === activeSlot;
                const count = Object.values(st.grimoire || {}).reduce((a, b) => a + b, 0);
                return (
                  <button key={i} onClick={() => loadSetIntoEditor(i)} style={{
                    flex: "1 1 180px", textAlign: "left", cursor: "pointer",
                    background: editing ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
                    border: "none",
                    borderTop: `1px solid ${editing ? GM.inkGoldBright : "rgba(226,232,244,0.18)"}`,
                    clipPath: facetTB(9), padding: "12px 14px",
                    boxShadow: editing
                      ? `inset 0 1px 0 rgba(245,216,120,0.35), inset 0 -4px 8px rgba(0,0,0,0.45)`
                      : "inset 0 1px 0 rgba(226,232,244,0.10), inset 0 -3px 6px rgba(0,0,0,0.4)",
                    filter: editing
                      ? `drop-shadow(0 0 10px ${GM.inkGoldBright}44) drop-shadow(0 2px 6px rgba(0,0,0,0.5))`
                      : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
                    fontFamily: GM.font,
                  }}>
                    <div style={{ fontSize: 8, letterSpacing: 4, color: GM.vellumFaint }}>
                      SET {i + 1}{i === seededSets.active ? " · EQUIPPED" : ""}
                    </div>
                    <div style={{
                      fontSize: 14, fontStyle: "italic", marginTop: 4,
                      color: editing ? "#ffe9a8" : GM.vellum,
                      textShadow: editing ? textGlow(GM.inkGoldBright) : TEXT_SET,
                    }}>{st.name || `Set ${i + 1}`}</div>
                    <div style={{ fontSize: 9, letterSpacing: 1, color: GM.vellumFaint, marginTop: 4 }}>
                      {count ? `${count} cards` : "empty"}
                      {st.companion ? ` · ${COMPANIONS[st.companion]?.name || "companion"}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 1, marginBottom: 22 }}>
              Tapping a set loads it here to edit. Nothing is written until you press ◆ Equip.
            </div>

            {/* ── NAME ─────────────────────────────────────── */}
            <SectionRule label="NAME THIS SET" />
            <input
              value={setName}
              maxLength={24}
              onChange={(e) => setSetName(e.target.value)}
              placeholder={`Set ${activeSlot + 1}`}
              style={{
                width: "100%", maxWidth: 340, boxSizing: "border-box",
                background: OBSIDIAN_RECESS, border: "none",
                borderTop: "1px solid rgba(0,0,0,0.5)",
                boxShadow: SHADOW_RECESS, clipPath: facetTB(7),
                color: "#ffe9a8", textShadow: textGlow(GM.inkGoldBright),
                fontFamily: GM.font, fontSize: 15, fontStyle: "italic",
                letterSpacing: 2, padding: "11px 14px", marginBottom: 24, outline: "none",
              }} />

            {/* ── THE OPENING RIFTS ────────────────────────── */}
            <SectionRule label={`THE OPENING RIFTS · ${ESCLING_SLOTS} ESCLINGS`} />
            <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 1, marginBottom: 14, maxWidth: 560, lineHeight: 1.6 }}>
              An Escling decides what that rift can Transcend into — the L1 must match its School.
              Four of the same is a board with one idea and no second plan; four different is four
              doors, and no promise that you will like what walks through any of them.
              Leave one open and the Forge derives it from your Grimoire, exactly as it always has.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 26 }}>
              {/* The battle line is ESCLING_SLOTS wide (rules.js, 2026-09-07 —
                  three rifts became four). A literal [0, 1, 2] here renders one
                  picker short of the board, so the fourth rift's Escling is a
                  choice the player is never offered and the duel derives for
                  them from the Grimoire. */}
              {Array.from({ length: ESCLING_SLOTS }, (_, slot) => (
                <SlotPicker
                  key={`esl-${slot}`}
                  label={`RIFT ${slot + 1}`}
                  // COMPANION_ILK_COLORS is keyed Space/Phantom/Dragon/Time
                  // — the SCHOOL axis — so an Escling's tint comes off
                  // `e.school` since the 2026-08-25 field-name fix. Reading
                  // `e.ilk` here would now hand it "Humanoid" and lose the
                  // tint entirely.
                  options={ESCLINGS.map(e => ({ id: e.id, name: e.name, tint: COMPANION_ILK_COLORS[e.school]?.primary }))}
                  value={esclings[slot]}
                  emptyLabel="◇ derive from deck"
                  onPick={(id) => setEsclings(prev => { const n = [...prev]; n[slot] = id; return n; })}
                />
              ))}
            </div>

            {/* ── CURIOS ───────────────────────────────────── */}
            <SectionRule label="CURIO RIFTS · 2" />
            <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 1, marginBottom: 14, maxWidth: 560, lineHeight: 1.6 }}>
              Curios are pre-placed and auto-activate at the opening of the Solrend. They are never deck contents.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 26 }}>
              {[0, 1].map(slot => (
                <SlotPicker
                  key={`cur-${slot}`}
                  label={`CURIO ${slot + 1}`}
                  options={CURIOS.filter(c => ownsCard(c.id)).map(c => ({ id: c.id, name: c.name, sub: c.effect }))}
                  value={curios[slot]}
                  emptyLabel="◇ empty rift"
                  onPick={(id) => setCurios(prev => {
                    const n = [...prev];
                    // One copy of a Curio, one rift. Picking a Curio
                    // that is already in the other rift MOVES it
                    // rather than duplicating it — the deck limit is
                    // 1 and two rifts holding the same card is a
                    // state the board would have to reject anyway.
                    const other = slot === 0 ? 1 : 0;
                    if (id && n[other] === id) n[other] = null;
                    n[slot] = id;
                    return n;
                  })}
                />
              ))}
            </div>

            {/* ── PROPHECY ─────────────────────────────────── */}
            <SectionRule label="PROPHECY · 1" />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 26 }}>
              <SlotPicker
                label="PROPHECY"
                wide
                options={PROPHECIES.filter(c => ownsCard(c.id)).map(c => ({ id: c.id, name: c.name, sub: c.effect }))}
                value={prophecy}
                emptyLabel="◇ no prophecy"
                onPick={setProphecy}
              />
            </div>

            {/* ── COMPANION ────────────────────────────────── */}
            <SectionRule label="COMPANION" />
            <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", letterSpacing: 1, marginBottom: 14, maxWidth: 560, lineHeight: 1.6 }}>
              A companion is a cosmetic and cosmetics equip anywhere and appear everywhere — so equipping this set
              re-asserts them across the whole Empire, not just at the board.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 30 }}>
              <SlotPicker
                label="COMPANION"
                wide
                options={unlockedCompanions.map(id => ({
                  id,
                  name: COMPANIONS[id]?.name || id,
                  sub: COMPANIONS[id]?.description,
                  tint: COMPANION_ILK_COLORS[COMPANIONS[id]?.ilk]?.primary,
                }))}
                value={companion}
                emptyLabel="◇ keep current"
                onPick={setCompanion}
              />
            </div>
          </div>
        )}

        {/* MY DECK tab */}
        {activeTab === "mydeck" && (
          <div>
            <div style={{
              fontSize: 8, letterSpacing: 5,
              color: GM.vellumFaint, marginBottom: 16,
              paddingBottom: 8, fontFamily: GM.font,
              borderBottom: `1px solid ${GM.ruling}`,
            }}>GRIMOIRE</div>
            {Object.keys(grimoire).length === 0 && (
              <div style={{ color: GM.vellumFaint, fontStyle: "italic",
                fontSize: 12, marginBottom: 24, letterSpacing: 1 }}>
                No cards added yet — add some from the Grimoire tab, or load a starter deck above.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }}>
              {ALL_GRIMOIRE_CARDS.filter(c => grimoire[c.id] > 0).map(card => (
                <CardDisplay key={card.id} card={card}
                  count={grimoire[card.id] || 0}
                  maxAllowed={card.deckLimit || 1}
                  owned={ownsCard(card.id)}
                  holoTier={primaryTierOf(card.id) || collection[card.id]?.holoTier || null}
                  availableTiers={availableTiersFor(card.id)}
                  onSelectHolo={selectHolo}
                  sourceTiers={collection[card.id]?.sourceTiers || null}
                  onSelectVariant={selectVariant}
                  cardSources={collection[card.id]?.sources || null}
                  onAdd={() => addCard(card.id, grimoire, setGrimoire, card.deckLimit || 1)}
                  onRemove={() => removeCard(card.id, grimoire, setGrimoire)} />
              ))}
            </div>

            <div style={{
              fontSize: 8, letterSpacing: 5,
              color: GM.vellumFaint, marginBottom: 16,
              paddingBottom: 8,
              borderBottom: `1px solid ${GM.ruling}`,
            }}>LUXMOIRE — IMPERA · U'HLTIMAS · AFFINITIES</div>
            {Object.keys(luxmoire).length === 0 && (
              <div style={{ color: GM.vellumFaint, fontStyle: "italic",
                fontSize: 12, letterSpacing: 1 }}>
                No Luxmoire cards added yet.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {ALL_LUXMOIRE_CARDS.filter(c => luxmoire[c.id] > 0).map(card => (
                <CardDisplay key={card.id} card={card}
                  count={luxmoire[card.id] || 0}
                  maxAllowed={card.deckLimit || 1}
                  owned={ownsCard(card.id)}
                  holoTier={primaryTierOf(card.id) || collection[card.id]?.holoTier || null}
                  availableTiers={availableTiersFor(card.id)}
                  onSelectHolo={selectHolo}
                  sourceTiers={collection[card.id]?.sourceTiers || null}
                  onSelectVariant={selectVariant}
                  cardSources={collection[card.id]?.sources || null}
                  onAdd={() => addCard(card.id, luxmoire, setLuxmoire, card.deckLimit || 1)}
                  onRemove={() => removeCard(card.id, luxmoire, setLuxmoire)} />
              ))}
            </div>
          </div>
        )}

        {/* GRIMOIRE / LUXMOIRE tabs */}
        {activeTab !== "mydeck" && (
          <div>
            {/* Search — minimal, engraved */}
            <div style={{ position: "relative", marginBottom: 14 }}>
              <input
                placeholder="Search cards..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: OBSIDIAN_RECESS,
                  border: `1px solid ${GM.pageBorder}`,
                  borderRadius: 10,
                  boxShadow: SHADOW_RECESS,
                  color: GM.vellum,
                  padding: "10px 14px 10px 36px",
                  fontFamily: GM.font, fontSize: 12,
                  letterSpacing: 1, outline: "none",
                  caretColor: GM.inkGold,
                }}
              />
              <span style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)",
                color: GM.inkBrown, fontSize: 13, pointerEvents: "none",
              }}>⌕</span>
            </div>

            {/* Filter panel — collapsible */}
            <div style={{ marginBottom: filtersOpen ? 0 : 4 }}>
              <button onClick={() => setFiltersOpen(v => !v)} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "transparent", border: "none", cursor: "pointer",
                padding: "6px 0 8px", color: GM.vellumFaint, fontFamily: GM.font,
                fontSize: 9, letterSpacing: 3,
              }}>
                <span>◆ FILTERS & SORT</span>
                <span style={{ fontSize: 11, transition: "transform 0.2s", display: "inline-block", transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
              </button>
            </div>
            {filtersOpen && <div style={{
              display: "flex", flexDirection: "column",
              gap: 12, marginBottom: 18,
              paddingBottom: 16,
              borderBottom: `1px solid ${GM.ruling}`,
            }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <Chips label="TYPE" value={filterType} onChange={handleTypeFilter} options={activeTab === "grimoire" ? [
                  { v: "All",       l: "All"      },
                  { v: "Escendant", l: "Being"    },
                  { v: "Tome",      l: "Tome"     },
                ] : [
                  { v: "All",       l: "All"      },
                  { v: "Impera",    l: "Impera"   },
                  { v: "U'hlti",   l: "U'hltima" },
                  { v: "Affinity",  l: "Affinity" },
                ]} colors={{ Escendant: SD.tealLight, Tome: SD.gold, "Impera": SD.purpleLight, "U'hlti": SD.goldBright, Affinity: SD.goldBright }} />
                <Chips label="LEVEL" value={filterLevel} onChange={setFilterLevel} options={[
                  { v: "All", l: "All" },
                  { v: "1",   l: "L1"  },
                  { v: "2",   l: "L2"  },
                  { v: "3",   l: "L3"  },
                ]} colors={{ "1": SD.tealLight, "2": SD.gold, "3": SD.redL }} />
              </div>

              {filterType === "Tome" && (
                <Chips label="TOME TYPE" value={filterTomeIlk} onChange={setFilterTomeIlk} options={[
                  { v: "All",             l: "All Tomes"       },
                  { v: "Construction",    l: "Construction"    },
                  { v: "Nihil",           l: "Nihil"           },
                  { v: "Omnia",           l: "Omnia"           },
                  { v: "Ancient Sorcery", l: "Ancient Sorcery" },
                  { v: "Ritual",          l: "Ritual"          },
                  { v: "Shadow Gambling", l: "Shadow Gambling" },
                  { v: "Contract",        l: "Contract"        },
                  { v: "Treasure",        l: "Treasure"        },
                ]} colors={{ Construction: SD.tealLight, Nihil: "#818cf8", Omnia: SD.redL, "Ancient Sorcery": SD.purpleLight, Ritual: SD.gold, "Shadow Gambling": SD.goldBright, Contract: SD.purpleLight, Treasure: SD.gold }} />
              )}

              {/* RELABELLED 2026-08-25: these four words are the SCHOOL
                  axis under the designer's ruling, and this chip row had
                  been telling the player they were Ilks. */}
              {(filterType === "Escendant" || filterType === "All") && (
                <Chips label="SCHOOL" value={filterSchool} onChange={setFilterSchool} options={[
                  { v: "All",     l: "All"     },
                  { v: "Space",   l: "Space"   },
                  { v: "Phantom", l: "Phantom" },
                  { v: "Time",    l: "Time"    },
                  { v: "Dragon",  l: "Dragon"  },
                ]} colors={{ Space: "#60a5fa", Phantom: "#a78bfa", Time: SD.tealLight, Dragon: SD.gold }} />
              )}

              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                <Chips label="SORT" value={sortBy} onChange={setSortBy} options={[
                  { v: "default",    l: "Default"  },
                  { v: "cost_asc",   l: "Cost ↑"   },
                  { v: "cost_desc",  l: "Cost ↓"   },
                  { v: "level_asc",  l: "Level ↑"  },
                  { v: "level_desc", l: "Level ↓"  },
                  { v: "lyt_desc",   l: "Lyt ↓"    },
                  { v: "name_asc",   l: "A–Z"      },
                ]} />
                {activeTab === "grimoire" && (
                  <Chips label="HOLO" value={filterHolo} onChange={setFilterHolo} options={[
                    { v: "All",            l: "All"        },
                    { v: "any_holo",       l: "Any ✦"      },
                    { v: "holo_silver",    l: "◈ Silver"   },
                    { v: "holo_gold",      l: "✦ Gold"     },
                    { v: "holo_mythic",    l: "◆ Mythic"   },
                    { v: "holo_void",      l: "◈ Void"     },
                    { v: "holo_solrend",   l: "◆ Solrend"  },
                    { v: "holo_aeternus",  l: "✦ A'eterni" },
                    { v: "holo_shadow",    l: "◇ Shadow"   },
                    { v: "holo_depths",   l: "◆ Depths"   },
                  ]} colors={{ any_holo: SD.gold, holo_silver: "#aaa", holo_gold: SD.gold, holo_mythic: "#ff88dd", holo_void: "#9333ea", holo_solrend: "#ff2266", holo_aeternus: "#f0f0ff", holo_shadow: "#9966ff", holo_depths: "#00aaff" }} />
                )}
                {isStoryMode && activeTab === "grimoire" && (
                  <button
                    onClick={() => setShowUnowned(v => !v)}
                    style={{
                      padding: "4px 14px", borderRadius: 20,
                      border: `1px solid ${showUnowned ? GM.inkGold : GM.inkBrown}`,
                      background: showUnowned ? `${SD.purpleLight}18` : "transparent",
                      color: showUnowned ? GM.inkGoldBright : GM.vellumDim,
                      cursor: "pointer", fontSize: 10,
                      fontWeight: showUnowned ? 900 : 400,
                      fontFamily: GM.font, letterSpacing: 1,
                    }}>
                    {showUnowned ? "◇ All Cards" : "◆ Owned Only"}
                  </button>
                )}
              </div>
            </div>}

            {/* Card count */}
            <div style={{
              fontSize: 8, color: GM.vellumFaint,
              letterSpacing: 4, marginBottom: 14,
              fontFamily: GM.font,
            }}>{displayCards.length} CARDS INSCRIBED</div>

            {/* Card grid */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {displayCards.map(card => {
                const deck    = activeTab === "grimoire" ? grimoire : luxmoire;
                const setDeck = activeTab === "grimoire" ? setGrimoire : setLuxmoire;
                return (
                  <CardDisplay
                    key={card.id} card={card}
                    count={deck[card.id] || 0}
                    maxAllowed={card.deckLimit || 1}
                    owned={ownsCard(card.id)}
                    holoTier={primaryTierOf(card.id) || collection[card.id]?.holoTier || null}
                    availableTiers={availableTiersFor(card.id)}
                    onSelectHolo={selectHolo}
                    sourceTiers={collection[card.id]?.sourceTiers || null}
                    onSelectVariant={selectVariant}
                    cardSources={collection[card.id]?.sources || null}
                    onAdd={() => addCard(card.id, deck, setDeck, card.deckLimit || 1)}
                    onRemove={() => removeCard(card.id, deck, setDeck)}
                    record={collection[card.id] || null}
                    scrap={scrapCtx}
                    marked={!!scrapMarks[card.id]}
                    onToggleMark={toggleScrapMark}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div>{/* /scroller */}

      {/* ── T21 · SCRAP ALL MARKED — one tap, every marked plain dupe.
          "So you're not clicking so much." Fixed bar, under the lightbox
          (z 590 < 600) so a scrap inspection can still float above it. */}
      {scrapCtx && markedIds.length > 0 && (
        <div style={{
          position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)",
          zIndex: 590, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          justifyContent: "center", maxWidth: "94vw",
          background: OBSIDIAN_PANEL, border: "1px solid #ff9c4a88", borderRadius: 14,
          padding: "10px 16px", boxShadow: SHADOW_FLOAT, fontFamily: SD.fontDisplay,
        }}>
          <span style={{ fontSize: 11, color: "#ff9c4a", fontWeight: 900, letterSpacing: 1 }}>
            ♻ {markedCopies} PLAIN DUPE{markedCopies === 1 ? "" : "S"} MARKED
          </span>
          <CoinChip amount={liveCoins} />
          <button disabled={scrapBusy} onClick={scrapAllMarked} style={{
            background: "#ff9c4a22", border: "1px solid #ff9c4a", color: "#ff9c4a",
            borderRadius: 8, padding: "8px 14px", cursor: scrapBusy ? "wait" : "pointer",
            fontFamily: SD.fontDisplay, fontSize: 11, fontWeight: 900, letterSpacing: 1,
            opacity: scrapBusy ? 0.6 : 1,
          }}>{scrapBusy ? "SCRAPPING…" : `SCRAP ALL MARKED → ◕${markedPay}`}</button>
          <button onClick={() => setScrapMarks({})} title="Clear marks" style={{
            background: "transparent", border: `1px solid ${SD.void4}`, color: SD.starDim,
            borderRadius: 8, padding: "8px 10px", cursor: "pointer", fontFamily: SD.fontDisplay, fontSize: 11,
          }}>✕</button>
        </div>
      )}
    </div>
    </div>
  );
}